import React from 'react';
import {
    Alert, Button, FormGroup, List, UncontrolledTooltip, Progress,
    Collapse, Row, Col, ListGroup, Nav, NavItem, NavLink, TabContent, TabPane,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faDownload,
    faExclamation,
    faMap,
    faPhone,
    faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { DEFAULT_LANGUAGE, LANGUAGE_LABELS, crewLeadLabel } from '@s20/i18n';
import { getWeekStartDate, resolveWeekStartsOn } from '@s20/common/util/date';
import { getTimezones } from '@s20/common/util/l10n';
import { clone } from '@s20/common/util/object';
import { email as emailUtil } from '@s20/common/util/validation';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm,
    FlexColumnCenter,
    FormCheckbox,
    ToastMessage,
    FormLabel,
    onReactSelectChanged,
    toasty,
    FlexRow,
    IconButton,
    Input,
    Textarea,
    InfoCheckbox,
    onFieldChange,
} from '../../common/forms/FormElements';
import EmailRecipients from '../../common/forms/EmailRecipients';
import { BaseFormViewModel } from '../../common/ViewModel';
import CommonContext, {
    ApiRoutes,
    SupportedFileExtensions,
    AppNavPaths,
} from '../../Common';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../../common/forms/ValidationError';
import { NumericInput } from '../../common/forms/NumericInput';
import FileUpload from '../../common/forms/FileUpload';
import { TenantSettings, CountyCaptureType, PayrollAppTypeOptions } from '../Tenant';
import TimeInput from '../../common/forms/TimeInput';
import { Block, BlockBorder } from '../../common/Block';
import { TipAlert } from '../../common/TipAlert';
import { PayPeriodTypes } from '../../organization/Organization';
import { TimesheetEmergencyPayTypes } from '../../payroll/Payroll';
import { SaveButton } from '../../common/forms/SaveButton';
import { SubHeading } from './SubHeading';
import { FeatureBadge, NewFieldBadge, SettingBadge } from './Badge';
import { ListGroupItem } from './ListGroupItem';
import { getAddressesAndValidity } from './EmailValidation';

const contractProjectsOptions = [
    {
        id: 1,
        name: 'Rentals Only',
        description: 'Projects are only used for rental contracts.',
    },
    {
        id: 2,
        name: 'All Contracts',
        description: 'Projects are used for all contracts.',
    },
];

const onKeyDown = (event) => {
    const maxCharPerLine = 75;
    const maxLines = 5;
    const textarea = event.target;
    const lines = textarea.value.split('\n');
    const cursorLine = textarea.value.substr(0, textarea.selectionStart).split('\n').length - 1;
    const currentLine = lines[cursorLine] ?? '';

    if (
        currentLine.length >= maxCharPerLine
        && event.key.length === 1
        && !event.ctrlKey
        && !event.metaKey
    ) {
        event.preventDefault();
    }

    if (event.key === 'Enter' && event.shiftKey && lines.length >= maxLines) {
        event.preventDefault();
    }
};

class TenantForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.asncnlRef = React.createRef();
        this.brandLogoUploadsRef = React.createRef();
        this.reportLogoUploadsRef = React.createRef();

        const stateBase = {
            daysOfWeek: [
                { id: 0, name: 'Sunday' },
                { id: 1, name: 'Monday' },
                { id: 2, name: 'Tuesday' },
                { id: 3, name: 'Wednesday' },
                { id: 4, name: 'Thursday' },
                { id: 5, name: 'Friday' },
                { id: 6, name: 'Saturday' },
            ],
            employeeGroups: [],
            optionalCompanyTypes: [],
            optionalContractScheduleTypes: [],
            optionalJobTypes: [],
            quickRentalContractTemplates: [],
            countyCaptureTypes: [],
            payPeriodTypes: [],
            activeHash: null,
            timesheetEmergencyPayTypes: [],
            useAsAppLogo: false,
            currentSubmittedTimesheetEmailList: null,
            currentDailyGSFReportEmailList: null,
            currentDailyOperationalReportEmailList: null,
            currentContractExpiryDistributionList: null,
            currentStaffingReportDistributionList: null,
            currentNewCustomerBillableHoursReportDistributionList: null,
            selectedReportDate: '',
            timezones: [],
            remittanceAddressOnPrintedInvoice: '',
            footerNotesOnPrintedInvoice: '',
            billingPeriodEndDate: null,
            availableLanguages: [],
            tenantLanguageIds: [],
            smsTemplates: {},
            smsTemplateActiveTab: '',
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.handleIntersection = this.handleIntersection.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        void this.ensureTenantContextAndPopulate();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            void this.ensureTenantContextAndPopulate();
        }
    }

    // Forces a full switch to the URL's tenant before rendering — avoids the
    // cross-tenant bugs caused when header- and URL-based tenant resolution disagree.
    async ensureTenantContextAndPopulate() {
        const targetTenantId = this.props.match.params.id;
        const sessionTenantId = window.sessionStorage.getItem('currentTenantId');
        const currentTenantId = sessionTenantId ?? this.context?.tenant?.id?.toString();

        if (
            targetTenantId != null
            && currentTenantId != null
            && targetTenantId.toString() === currentTenantId.toString()
        ) {
            return this.populateState();
        }

        try {
            const baseUrl = await httpClient.put(
                ApiRoutes.auth.updateTenant(targetTenantId),
                {},
                httpClient.format.json,
            );
            window.sessionStorage.setItem('currentTenantId', targetTenantId);

            if (typeof baseUrl === 'string' && baseUrl && baseUrl !== '/') {
                // Subdomain mode: each tenant lives on its own host, so we navigate to it.
                const settingsPath = `${AppNavPaths.Tenant}/${targetTenantId}`;
                const dest = baseUrl.replace(/\/$/, '') + settingsPath;
                const impersonatingUserId = window.sessionStorage.getItem('impersonating');
                const destUrl = impersonatingUserId
                    ? `${dest}${dest.includes('?') ? '&' : '?'}impersonating=${encodeURIComponent(impersonatingUserId)}`
                    : dest;
                window.location.replace(destUrl);
            } else {
                window.location.reload();
            }
        } catch (err) {
            console.error('Failed to switch tenant context for settings:', err);
            return this.populateState();
        }
        return undefined;
    }

    onClearErrors = () => this.setState({ errors: {} });

    renderSettingRefusal = (field) => {
        const refusal = this.state.errors?.[field];

        if (!refusal) {
            return null;
        }

        return (
            <Alert
                color="danger"
                className="mt-2"
            >
                <FontAwesomeIcon
                    icon={faExclamation}
                    className="mr-2"
                />
                {refusal}
            </Alert>
        );
    };

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onDelete = async () => {
        const { organization } = this.state;
        const response = await httpClient
            .delete(ApiRoutes.company.delete(organization.id))
            .catch(this.onCatchFetchError);
        if (response) this.onClose(response);
    };

    onEmpOnboardingCheckedChanged = (event) => {
        const value = event.target.checked.toString();
        const { tenant } = { ...this.state };
        const { tenantSettings } = { ...tenant };

        for (const setting of tenantSettings) {
            if (setting.name === TenantSettings.EmployeeOnboardingEnabled) {
                setting.value = value;
            } else if (
                setting.name
                === TenantSettings.EmployeeOnboardingDefaultGroup
                && value === 'false'
            ) {
                setting.value = null; // clear out emp group if disabling onboarding.
            }
        }

        tenant.tenantSettings = tenantSettings;
        this.setState({ tenant });
    };

    onChange = onFieldChange;
    onSelectChange = onReactSelectChanged;

    onSettingChanged = (name, value) => {
        const { tenant } = this.state;

        let setting = tenant.tenantSettings.find((s) => s.name === name);

        if (setting) {
            setting.value = value;
            this.setState({ tenant });
        } else {
            setting = {
                name,
                value,
            };

            this.setState({ tenant: { ...tenant, tenantSettings: [...tenant.tenantSettings, setting] } });
        }
    };

    downloadDailyOperationalReport = async (e, customDate = null) => {
        e.preventDefault();

        try {
            const { id } = this.state.tenant;
            const dateIso = customDate !== null ? customDate : this.state.selectedReportDate;

            // Check if we're in production environment
            const isProduction = import.meta.env.PROD;

            let url;
            if (!isProduction) {
                url = ApiRoutes.report.downloadDailyOperationalReportExcel(id);
                const fileDate = dateIso || new Date().toISOString().split('T')[0];
                const filename = `DailyOperationalReport_${fileDate}.xlsx`;
                const body = dateIso ? { dateIso: new Date(dateIso).toISOString() } : null;
                await httpClient.downloadFile(url, body, filename);

                const message = dateIso
                    ? `Daily operational report for ${dateIso} has been downloaded.`
                    : 'Daily operational report has been downloaded.';

                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Success"
                        message={message}
                    />,
                );
            } else {
                url = ApiRoutes.report.downloadDailyOperationalReport(id);
                const body = dateIso ? { dateIso: new Date(dateIso).toISOString() } : null;
                await httpClient.post(url, body);

                const message = dateIso
                    ? `Daily operational report for ${dateIso} has been queued and will be sent to your email.`
                    : 'Daily operational report has been queued and will be sent to your email.';

                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Success"
                        message={message}
                    />,
                );
            }
        } catch {
            toast.error(
                <ToastMessage
                    icon={faExclamation}
                    header="Error"
                    message="An error occurred while processing the daily operational report."
                />,
            );
        }
    };

    onReportDateChange = (e) => {
        this.setState({ selectedReportDate: e.target.value });
    };

    handleIntersection = (id) => {
        this.setState({ activeHash: id });
    };

    createRentalInvoices = async () => {
        try {
            this.setSaving(true);
            const { tenant, billingPeriodEndDate } = this.state;
            const { id } = tenant;
            const url = ApiRoutes.invoice.createRentalInvoiceTask(id);

            await httpClient.post(url, billingPeriodEndDate);
            this.setSaving(false);
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Success"
                    message="Rental Invoices task has been created."
                />,
            );
        } catch (error) {
            this.setSaving(false);
            console.error(error);
            toast.error(
                <ToastMessage
                    icon={faExclamation}
                    header="Error"
                    message="An error occurred while creating the Rental Invoices task."
                />,
            );
        }
    };

    importADPEmployees = async () => {
        try {
            this.setSaving(true);
            const { id, companyName } = this.state.tenant;

            const response = await httpClient.get(
                ApiRoutes.employeeImport.exportADPEmployees(id),
                httpClient.format.none,
            );
            this.setSaving(false);
            if (response.ok) {
                const blob = await response.blob();
                const a = document.createElement('a');
                const url = URL.createObjectURL(blob);
                a.href = url;
                a.download = `${companyName} - ADP Worker Mapping.xlsx`;
                a.click();
                URL.revokeObjectURL(url);

                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Success"
                        message="ADP Employee File has been downloaded."
                    />,
                );
            } else {
                toast.error(
                    <ToastMessage
                        icon={faExclamation}
                        header="Error"
                        message="An error occurred while creating the ADP Employee File."
                    />,
                );
            }
        } catch (error) {
            this.setSaving(false);
            console.error(error);
            toast.error(
                <ToastMessage
                    icon={faExclamation}
                    header="Error"
                    message="An error occurred while creating the ADP Employee File."
                />,
            );
        }
    };

    onSubmit = async () => {
        const expiryListErrors = this.findContractExpiryListErrors();

        if (expiryListErrors) {
            toast.error(
                <ToastMessage
                    icon={faExclamation}
                    header="Contract Expiry Distribution List Invalid"
                    message={expiryListErrors}
                />,
            );

            return;
        }

        const staffingReportListErrors = this.findStaffingReportDistributionListErrors();

        if (staffingReportListErrors) {
            toast.error(
                <ToastMessage
                    icon={faExclamation}
                    header="Staffing Report Distribution List Invalid"
                    message={staffingReportListErrors}
                />,
            );

            return;
        }

        const newCustomerBillableHoursListErrors = this.findNewCustomerBillableListErrors();

        if (newCustomerBillableHoursListErrors) {
            toast.error(
                <ToastMessage
                    icon={faExclamation}
                    header="New Customer Billable Hours Report Distribution List Invalid"
                    message={newCustomerBillableHoursListErrors}
                />,
            );

            return;
        }

        // Clear any fluent api errors
        this.onClearErrors();
        this.setSaving(true);

        const { tenant, smsTemplates, tenantLanguageIds } = this.state;

        // The settings save goes first: the server can refuse it (the pay calendar lock), and a refused
        // save must leave nothing of the form committed. Supported languages and the per-template SMS
        // bodies are independent endpoints, saved in parallel only once the settings are in. Empty-string
        // '' lngKey is the language-agnostic / legacy row (null LanguageId on the server).
        const response = await httpClient
            .put(ApiRoutes.tenant.settingsById(tenant.id), tenant)
            .catch(this.handleSaveError);

        if (response && parseInt(response.id ?? 0) >= 0) {
            await Promise.all([
                httpClient.put(ApiRoutes.language.tenant(), tenantLanguageIds ?? []),
                ...Object.entries(smsTemplates ?? {}).map(([key, values]) => httpClient.put(
                    ApiRoutes.language.tenantSmsTemplateByKey(key),
                    Object.entries(values ?? {}).map(([lngKey, value]) => ({
                        languageId: lngKey === '' ? null : parseInt(lngKey, 10),
                        value,
                    })),
                )),
            ]);

            const { payCalendarWarning, ...savedTenant } = response;

            this.setState({
                tenant: savedTenant,
                originalData: savedTenant,
                saving: false,
                formValidated: false,
            });

            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message={`Tenant settings for [${response.companyName}] saved.`}
                />,
            );

            if (payCalendarWarning) {
                toast.warning(
                    <ToastMessage
                        icon={faExclamation}
                        header="Check the week start and Sunday premium"
                        message={payCalendarWarning}
                    />,
                    { autoClose: false },
                );
            }
        }

        this.setSaving(false);
    };

    getTenantSetting = (settingName) => {
        const { tenant } = { ...this.state };
        const { tenantSettings } = { ...tenant };

        const ret = { ...tenantSettings.find((x) => x.name === settingName) }
            .value;

        return ret;
    };

    setSaving = (b) => this.setState({ saving: b });

    addLogoFormData(_file, formData) {
        const { useAsAppLogo } = this.state;
        formData.append('useAsAppLogo', !!useAsAppLogo);
    }

    onEmailAdded = (tenantSetting, stateKey) => {
        const currentEmail = this.state[stateKey];
        if (currentEmail) {
            const emailAddress = currentEmail.trim();
            const match = emailUtil(emailAddress);
            const value = this.getTenantSetting(tenantSetting);

            if (match) {
                const newValue = value?.length ? value.split(',') : [];
                if (!newValue.includes(emailAddress)) {
                    newValue.push(emailAddress);
                }
                this.onSettingChanged(tenantSetting, newValue.join(','));
                this.setState({
                    [stateKey]: null,
                });
            } else {
                toasty.error('Please enter a single valid email address.');
            }
        }
    };

    onEmailChanged = (stateKey) => (e) => {
        const val = e.currentTarget.value;
        this.setState({ [stateKey]: val });
    };


    onEmailRemoved = (tenantSetting) => (selected) => {
        const value = this.getTenantSetting(tenantSetting);
        const valueArr = value.split(',');
        const index = valueArr.findIndex(
            (ea) => ea === selected,
        );
        valueArr.splice(index, 1);
        this.onSettingChanged(tenantSetting, valueArr.join(','));
    };

    findContractExpiryListErrors = () => {
        if (
            (this.getTenantSetting(TenantSettings.EnableContractDateRange)
                ?? 'false') === 'false'
        ) {
            return null;
        }

        const contractExpiryDistributionList = this.getTenantSetting(
            TenantSettings.ContractExpiryDistributionList,
        );

        if (!contractExpiryDistributionList) {
            return 'Expiration distribution list must be set.';
        }

        const validatedEmailAddresses = getAddressesAndValidity(
            contractExpiryDistributionList,
        );

        if (validatedEmailAddresses.length == 0) {
            return 'One or more email addresses must be provided.';
        }

        if (validatedEmailAddresses.every((vea) => vea.isValid)) {
            return null;
        }

        const invalidAddresses = validatedEmailAddresses.filter(
            (vea) => !vea.isValid,
        );

        return `The following email address(es) are invalid: [${invalidAddresses
            .map((vea) => vea.emailAddress)
            .join(', ')}]`;
    };

    findNewCustomerBillableListErrors = () => {
        if (
            (this.getTenantSetting(
                TenantSettings.EnableNewCustomerBillableHoursReport,
            ) ?? 'false') === 'false'
        ) {
            return null;
        }

        const newCustomerBillableHoursReportDistributionList = this.getTenantSetting(
            TenantSettings.NewCustomerBillableHoursReportDistributionList,
        );

        if (!newCustomerBillableHoursReportDistributionList) {
            return 'New customer billable hours report distribution list must be set.';
        }

        const validatedEmailAddresses = getAddressesAndValidity(
            newCustomerBillableHoursReportDistributionList,
        );

        if (validatedEmailAddresses.length == 0) {
            return 'One or more email addresses must be provided.';
        }

        if (validatedEmailAddresses.every((vea) => vea.isValid)) {
            return null;
        }

        const invalidAddresses = validatedEmailAddresses.filter(
            (vea) => !vea.isValid,
        );

        return `The following email address(es) are invalid: [${invalidAddresses
            .map((vea) => vea.emailAddress)
            .join(', ')}]`;
    };

    findStaffingReportDistributionListErrors = () => {
        if (
            (this.getTenantSetting(TenantSettings.EnableStaffingReport)
                ?? 'false') === 'false'
        ) {
            return null;
        }

        const staffingReportDistributionList = this.getTenantSetting(
            TenantSettings.StaffingReportDistributionList,
        );

        if (!staffingReportDistributionList) {
            return 'Expiration distribution list must be set.';
        }

        const validatedEmailAddresses = getAddressesAndValidity(
            staffingReportDistributionList,
        );

        if (validatedEmailAddresses.length == 0) {
            return 'One or more email addresses must be provided.';
        }

        if (validatedEmailAddresses.every((vea) => vea.isValid)) {
            return null;
        }

        const invalidAddresses = validatedEmailAddresses.filter(
            (vea) => !vea.isValid,
        );

        return `The following email address(es) are invalid: [${invalidAddresses
            .map((vea) => vea.emailAddress)
            .join(', ')}]`;
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    async populateState() {
        const tenantId = this.props.match.params.id;
        let [
            tenant,
            groups,
            optionalCompanyTypes,
            optionalContractScheduleTypes,
            optionalJobTypes,
            countyCaptureTypes,
            payPeriodTypes,
            timesheetEmergencyPayTypes,
            timezones,
            availableLanguages,
            tenantLanguages,
            smsTemplates,
            quickRentalContractTemplates,
        ] = await Promise.all([
            httpClient.js(ApiRoutes.tenant.byId(tenantId)),
            httpClient.js(ApiRoutes.auth.groupsByTenantId(tenantId)),
            httpClient.js(ApiRoutes.tenant.optionalCompanyTypes()),
            httpClient.js(ApiRoutes.tenant.optionalContractScheduleTypes()),
            httpClient.js(ApiRoutes.tenant.optionalJobTypes()),
            httpClient.js(ApiRoutes.tenant.countyCaptureTypes()),
            httpClient.js(ApiRoutes.typeAheads.payPeriodTypes()),
            httpClient.js(ApiRoutes.typeAheads.timesheetEmergencyPayTypes()),
            getTimezones(),
            httpClient.js(ApiRoutes.language.all()).catch(() => []),
            httpClient.js(ApiRoutes.language.tenant()).catch(() => []),
            httpClient.js(ApiRoutes.language.tenantSmsTemplates()).catch(() => []),
            httpClient.js(ApiRoutes.typeAheads.quickRentalContractTemplates()).catch(() => []),
        ]);

        groups = groups.filter((x) => x.groupName !== 'Administrator');

        const originalData = clone(tenant);

        this.setState({
            originalData,
            tenant,
            loading: false,
            saving: false,
            employeeGroups: groups,
            optionalCompanyTypes,
            optionalContractScheduleTypes,
            optionalJobTypes,
            countyCaptureTypes,
            payPeriodTypes,
            timesheetEmergencyPayTypes,
            timezones,
            availableLanguages: availableLanguages ?? [],
            tenantLanguageIds: (tenantLanguages ?? []).map((l) => l.id),
            // API returns a flat list [{templateKey, languageId, value}]; group by
            // templateKey for the editor's per-tab lookup. Legacy/null languageId
            // maps to '' to share storage with the English tab.
            smsTemplates: (smsTemplates ?? []).reduce((acc, row) => {
                const lngKey = row.languageId == null ? '' : row.languageId.toString();
                acc[row.templateKey] = { ...(acc[row.templateKey] || {}), [lngKey]: row.value };
                return acc;
            }, {}),
            smsTemplateActiveTab: ((tenantLanguages ?? [])[0]?.id ?? '').toString(),
            quickRentalContractTemplates: quickRentalContractTemplates ?? [],
        });
    }

    onSmsTemplateChanged = (templateKey, languageIdStr, value) => {
        const { smsTemplates } = this.state;
        const next = { ...smsTemplates };
        next[templateKey] = { ...(next[templateKey] || {}), [languageIdStr]: value };
        this.setState({ smsTemplates: next });
    };

    renderSmsTemplatesEditor = (legacy) => {
        const {
            availableLanguages,
            tenantLanguageIds,
            smsTemplates,
            smsTemplateActiveTab,
        } = this.state;

        const supportedLanguages = (availableLanguages ?? [])
            .filter((lng) => tenantLanguageIds.includes(lng.id));
        const englishOnly = (availableLanguages ?? []).filter((lng) => lng.name === DEFAULT_LANGUAGE);
        const tabsToRender = supportedLanguages.length > 0 ? supportedLanguages : englishOnly;
        if (tabsToRender.length === 0) return null;

        const templateRows = [
            { key: TenantSettings.JobAssignmentSMSTemplate, label: 'Job Assignment SMS Template', legacy: legacy.assignment },
            { key: TenantSettings.JobAssignmentOverrideSMSTemplate, label: 'Job Assignment Override SMS Template', legacy: legacy.override },
            { key: TenantSettings.JobAssignmentCancellationSMSTemplate, label: 'Job Assignment Cancellation SMS Template', legacy: legacy.cancellation },
            { key: TenantSettings.JobAssignmentClientCancellationSMSTemplate, label: 'Job Assignment Client Cancellation SMS Template', legacy: legacy.clientCancellation },
            { key: TenantSettings.TimesheetRejectSMSTemplate, label: 'Timesheet Reject SMS Template', legacy: legacy.timesheetReject },
        ];

        // English maps to the language-agnostic legacy row (empty-string key, NULL
        // LanguageId on the server) so a single edit covers both English-preferring
        // recipients and recipients with no preference set.
        const storeKeyFor = (lng) => (lng.name === DEFAULT_LANGUAGE ? '' : lng.id.toString());

        const valueFor = (templateKey, lng, fallback) => {
            const stored = smsTemplates?.[templateKey]?.[storeKeyFor(lng)];
            if (stored != null) return stored;
            return lng.name === DEFAULT_LANGUAGE ? (fallback ?? '') : '';
        };

        const renderPaneContents = (lng) => templateRows.map((row) => (
            <Textarea
                key={row.key}
                label={row.label}
                id={`${row.key}_${lng.id}`}
                name={`${row.key}_${lng.id}`}
                rows="3"
                onChange={(event) => this.onSmsTemplateChanged(row.key, storeKeyFor(lng), event.target.value)}
                value={valueFor(row.key, lng, row.legacy)}
            />
        ));

        const activeTabId = smsTemplateActiveTab || tabsToRender[0].id.toString();
        const showTabs = tabsToRender.length > 1;

        return (
            <Block component={FormGroup}>
                {showTabs && (
                    <>
                        <Nav tabs>
                            {tabsToRender.map((lng) => {
                                const id = lng.id.toString();
                                return (
                                    <NavItem key={id}>
                                        <NavLink
                                            href="#"
                                            active={activeTabId === id}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                this.setState({ smsTemplateActiveTab: id });
                                            }}
                                        >
                                            {LANGUAGE_LABELS[lng.name] || lng.description || lng.name}
                                        </NavLink>
                                    </NavItem>
                                );
                            })}
                        </Nav>
                        <TabContent
                            activeTab={activeTabId}
                            className="pt-3"
                        >
                            {tabsToRender.map((lng) => (
                                <TabPane
                                    key={lng.id}
                                    tabId={lng.id.toString()}
                                >
                                    {renderPaneContents(lng)}
                                </TabPane>
                            ))}
                        </TabContent>
                    </>
                )}
                {!showTabs && renderPaneContents(tabsToRender[0])}
            </Block>
        );
    };

    updateTenantDispatchTimezones = async () => {
        this.setSaving(true);
        const { tenant } = this.state;
        const url = ApiRoutes.tenant.updateDispatchTimezones(tenant.id);
        await httpClient.get(url);
        this.setSaving(false);
    };

    updateTenantEmployeeTimezones = async () => {
        this.setSaving(true);
        const { tenant } = this.state;
        const url = ApiRoutes.tenant.updateEmployeesTimezones(tenant.id);
        await httpClient.get(url);
        this.setSaving(false);
    };

    remittanceAddressOnChange = (event) => {
        const { value } = event.target;

        this.onSettingChanged(
            TenantSettings.RemittanceAddressOnPrintedInvoice,
            value.replace(/\r?\n/g, '\\n'),
        );
    };

    footerNotesOnChange = (event) => {
        const { value } = event.target;

        this.onSettingChanged(
            TenantSettings.FooterNotesOnPrintedInvoice,
            value.replace(/\r?\n/g, '\\n'),
        );
    };

    resetForm = () => this.setState({ formValidated: false });

    render() {
        const {
            loading,
            daysOfWeek,
            employeeGroups,
            tenant,
            originalData,
            errors,
            formValidated,
            saving,
            optionalCompanyTypes,
            optionalContractScheduleTypes,
            optionalJobTypes,
            countyCaptureTypes,
            payPeriodTypes,
            timesheetEmergencyPayTypes,
            useAsAppLogo,
            currentSubmittedTimesheetEmailList,
            currentDailyGSFReportEmailList,
            currentDailyOperationalReportEmailList,
            currentContractExpiryDistributionList,
            currentStaffingReportDistributionList,
            currentNewCustomerBillableHoursReportDistributionList,
            timezones,
            activeHash,
            billingPeriodEndDate,
            availableLanguages,
            tenantLanguageIds,
            quickRentalContractTemplates,
        } = this.state;

        if (
            loading
            || !this.context?.permissions
            || !tenant
            || !employeeGroups
        ) {
            return <Progress />;
        }
        const companyName = tenant.companyName || '';

        const empOnboardingEnabled = (this.getTenantSetting(
            TenantSettings.EmployeeOnboardingEnabled,
        ) ?? 'false') === 'true';
        const currentOnboardEmpGroup = (this.state.employeeGroups ?? []).find(
            (x) => x.id
                === parseInt(
                    this.getTenantSetting(
                        TenantSettings.EmployeeOnboardingDefaultGroup,
                    ) ?? -1,
                ),
        ) ?? '';
        const jobUploadsEnabled = this.getTenantSetting(
            TenantSettings.JobUploadsEnabled ?? 'false',
        ) === 'true';

        const allowRentalChargeTypes = this.getTenantSetting(
            TenantSettings.AllowRentalChargeTypes ?? 'false',
        ) === 'true';

        const weekStartsOnSetting = this.getTenantSetting(TenantSettings.WeekStartsOn);
        const weekStartsOn = resolveWeekStartsOn({ payPeriodBeginDayOfWeek: weekStartsOnSetting });
        const weekStartDay = (this.state.daysOfWeek ?? []).find(
            (x) => x.id === parseInt(weekStartsOnSetting ?? -1),
        ) ?? '';
        // The week always ends on the day before it starts.
        const weekEndDay = (this.state.daysOfWeek ?? []).find((x) => x.id === (weekStartsOn + 6) % 7);
        const biweeklyPayPeriodStartDate = this.getTenantSetting(
            TenantSettings.BiweeklyPayPeriodStartDate,
        );
        const timesheetAdjustmentsEnabled = (this.getTenantSetting(
            TenantSettings.TimesheetAdjustmentsEnabled,
        ) ?? 'false') === 'true';

        const hoursEnabled = (this.getTenantSetting(TenantSettings.DispatchHoursEnabled)
            ?? 'false') === 'true';

        const rollUpDriveTimeEnabled = (this.getTenantSetting(TenantSettings.RollUpDriveTime)
            ?? 'false') === 'true';
        const attendanceEventsEnabled = (this.getTenantSetting(
            TenantSettings.AttendanceEventsEnabled,
        ) ?? 'false') === 'true';
        const assignmentCancelThresh = Number(
            this.getTenantSetting(
                TenantSettings.AssignmentCancellationThreshold,
            ) ?? null,
        ) ?? '';
        const assignmentCancelMin = Number(
            this.getTenantSetting(
                TenantSettings.AssignmentCancellationMinimumCharge,
            ) ?? null,
        ) ?? '';
        const timesheetEmergencyEnabled = (this.getTenantSetting(
            TenantSettings.TimesheetEmergencyEnabled,
        ) ?? 'false') === 'true';
        const timesheetEmergencyStart = this.getTenantSetting(
            TenantSettings.TimesheetEmergencyStart,
        );
        const timesheetEmergencyEnd = this.getTenantSetting(
            TenantSettings.TimesheetEmergencyEnd,
        );

        const timesheetEmergencyPayTypeId = parseInt(
            this.getTenantSetting(TenantSettings.TimesheetEmergencyPayTypeId) ?? 1,
        );

        const timesheetEmergencyPayAdjustment = parseFloat(this.getTenantSetting(TenantSettings.TimesheetEmergencyPayAdjustment) ?? '0.0m');

        const billingEnabled = (this.getTenantSetting(TenantSettings.BillingEnabled)
            ?? 'false') === 'true';
        const billingRatesEnabled = (this.getTenantSetting(TenantSettings.BillingRatesEnabled)
            ?? 'false') === 'true';
        const payrollEnabled = (this.getTenantSetting(TenantSettings.PayrollEnabled)
            ?? 'false') === 'true';
        const timesheetEmailEnabled = (this.getTenantSetting(TenantSettings.TimesheetEmailEnabled)
            ?? 'false') === 'true';
        const timesheetExportEnabled = (this.getTenantSetting(TenantSettings.TimesheetExportEnabled)
            ?? 'false') === 'true';
        const timesheetUseJobCity = (this.getTenantSetting(TenantSettings.TimesheetUseJobCity)
            ?? 'false') === 'true';
        const timesheetEquipmentAuditEnabled = (this.getTenantSetting(TenantSettings.TimesheetEquipmentAuditEnabled)
            ?? 'false') === 'true';

        const autoCalculateDriveTimeBilling = (this.getTenantSetting(
            TenantSettings.AutoCalculateDriveTimeBilling,
        ) ?? 'false') === 'true';
        const blockBillingCancelEnabled = (this.getTenantSetting(
            TenantSettings.BlockBillingCancelEnabled,
        ) ?? 'false') === 'true';
        const blockPayrollCancelEnabled = (this.getTenantSetting(
            TenantSettings.BlockPayrollCancelEnabled,
        ) ?? 'false') === 'true';
        const useStateMinimum = (this.getTenantSetting(TenantSettings.UseStateMinimum)
            ?? 'false') === 'true';
        const enableAlternateBillTo = (this.getTenantSetting(TenantSettings.EnableAlternateBillTo)
            ?? 'false') === 'true';
        const enablePredefinedTimesheetMissingSigReason = (this.getTenantSetting(
            TenantSettings.EnablePredefinedTimesheetMissingSigReason,
        ) ?? 'false') === 'true';
        const invoicingEnabled = (this.getTenantSetting(TenantSettings.InvoicingEnabled)
            ?? 'false') === 'true';
        const chargeTypeGroupsEnabled = (this.getTenantSetting(
            TenantSettings.ChargeTypeGroupsEnabled,
        ) ?? 'false') === 'true';
        const allowCrossDispatching = (this.getTenantSetting(TenantSettings.AllowCrossDispatching)
            ?? 'false') === 'true';
        const enableSelfDispatchTimesheets = (this.getTenantSetting(
            TenantSettings.EnableSelfDispatchTimesheets,
        ) ?? 'false') === 'true';
        const dispatchStatusFilterEnabled = (this.getTenantSetting(
            TenantSettings.DispatchStatusFilterEnabled,
        ) ?? 'false') === 'true';
        const contactScreenEnabled = (this.getTenantSetting(TenantSettings.ContactScreenEnabled)
            ?? 'false') === 'true';
        const contactActivitiesEnabled = contactScreenEnabled
            && (this.getTenantSetting(
                TenantSettings.ContactActivitiesEnabled,
            ) ?? 'false') === 'true';

        const constrainContactTypesEnabled = (this.getTenantSetting(TenantSettings.ConstrainContactTypesOnJobEnabled)
            ?? 'false') === 'true';

        const employeeLongTermStatusManagementEnabled = (this.getTenantSetting(
            TenantSettings.EmployeeLongTermStatusManagementEnabled,
        ) ?? 'false') === 'true';
        const enableCertification = (this.getTenantSetting(
            TenantSettings.EnableCertification,
        ) ?? 'false') === 'true';
        const enableCustomizedHoliday = (this.getTenantSetting(
            TenantSettings.EnableCustomizedHoliday,
        ) ?? 'false') === 'true';

        const forceAllChargesInTimesheetExport = (this.getTenantSetting(
            TenantSettings.ForceAllChargesInTimesheetExport,
        ) ?? 'false') === 'true';

        const useLimitedApprovals = (this.getTenantSetting(TenantSettings.UseLimitedApprovals)
            ?? 'false') === 'true';
        const captureCounty = parseInt(
            this.getTenantSetting(TenantSettings.CaptureCounty) ?? 1,
        );
        const payPeriodTypeId = parseInt(
            this.getTenantSetting(TenantSettings.PayPeriodTypeId) ?? 1,
        );
        const enableContractDateRange = (this.getTenantSetting(
            TenantSettings.EnableContractDateRange,
        ) ?? 'false') === 'true';
        const allowTimesheetStatusRollbacks = (this.getTenantSetting(
            TenantSettings.AllowTimesheetStatusRollbacks,
        ) ?? 'false') === 'true';

        const showContractDescriptionInTimesheetView = (this.getTenantSetting(
            TenantSettings.ShowContractDescriptionInTimesheetView,
        ) ?? 'false') === 'true';

        const contractExpiryCompanyName = this.getTenantSetting(
            TenantSettings.ContractExpiryCompanyName,
        );

        const googleMapsApiKey = this.getTenantSetting(
            TenantSettings.GoogleMapsApiKey,
        );

        const includeSchedulingOnDailyJobExport = (this.getTenantSetting(
            TenantSettings.IncludeSchedulingOnDailyJobExport,
        ) ?? 'false') === 'true';

        const showLongTermStatusCountOnRoster = (this.getTenantSetting(
            TenantSettings.ShowLongTermStatusCountOnRoster,
        ) ?? 'false') === 'true';

        const allowDefaultChargeTypeValues = (this.getTenantSetting(
            TenantSettings.AllowDefaultChargeTypeValues,
        ) ?? 'false') === 'true';

        const enableRequestedPersonnel = (this.getTenantSetting(
            TenantSettings.EnableRequestedPersonnel,
        ) ?? 'false') === 'true';

        const enableDriverReportingInTimesheet = (this.getTenantSetting(
            TenantSettings.EnableDriverReportingInTimesheet,
        ) ?? 'false') === 'true';

        const enableAFADChargeTypeGrouping = (this.getTenantSetting(
            TenantSettings.EnableAFADChargeTypeGrouping,
        ) ?? 'false') === 'true';

        const enableJobOverrides = (this.getTenantSetting(
            TenantSettings.EnableJobOverrides,
        ) ?? 'false') === 'true';

        const enableDoubleTimeBilling = (this.getTenantSetting(
            TenantSettings.EnableDoubleTimeBilling,
        ) ?? 'false') === 'true';

        const enableDoubleTimePayroll = (this.getTenantSetting(
            TenantSettings.EnableDoubleTimePayroll,
        ) ?? 'false') === 'true';

        const enableAlternativePayrollBilling = (this.getTenantSetting(
            TenantSettings.EnableAlternativePayrollBilling,
        ) ?? 'false') === 'true';

        const supportedFiles = SupportedFileExtensions.image;

        const bannerMessage = this.getTenantSetting(
            TenantSettings.BannerMessage,
        );

        const bannerMessageExternallyVisible = (this.getTenantSetting(
            TenantSettings.BannerMessageExternallyVisible,
        ) ?? 'false') === 'true';

        const enablePrevailingWageContracts = (this.getTenantSetting(
            TenantSettings.EnablePrevailingWageContracts,
        ) ?? 'false') === 'true';

        const gLStringsEnabled = (this.getTenantSetting(TenantSettings.GLStringsEnabled)
            ?? 'false') === 'true';

        const activeEmployeeListEnabled = (this.getTenantSetting(
            TenantSettings.ActiveEmployeeListEnabled,
        ) ?? 'false') === 'true';

        const enableNewCustomerBillableHoursReport = (this.getTenantSetting(
            TenantSettings.EnableNewCustomerBillableHoursReport,
        ) ?? 'false') === 'true';

        const enableStaffingReport = (this.getTenantSetting(TenantSettings.EnableStaffingReport)
            ?? 'false') === 'true';

        const quirksEnabled = (this.getTenantSetting(TenantSettings.QuirksEnabled)
            ?? 'false') === 'true';

        const enforceContractUnionWork = (this.getTenantSetting(
            TenantSettings.EnforceContractUnionWork,
        ) ?? 'false') === 'true';

        const timesheetEmailReplyTo = this.getTenantSetting(
            TenantSettings.TimesheetEmailReplyTo,
        );

        const timesheetEmailBcc = this.getTenantSetting(
            TenantSettings.TimesheetEmailBcc,
        );

        const timesheetEmailFromName = this.getTenantSetting(
            TenantSettings.TimesheetEmailFromName,
        );

        const enableDriverRideConfirmationFields = (this.getTenantSetting(
            TenantSettings.EnableDriverRideConfirmationFields,
        ) ?? 'false') === 'true';

        const enableRegularShiftDifferential = (this.getTenantSetting(
            TenantSettings.EnableRegularShiftDifferential,
        ) ?? 'false') === 'true';

        const regularShiftDifferentialStart = this.getTenantSetting(
            TenantSettings.RegularShiftDifferentialStart,
        );
        const regularShiftDifferentialEnd = this.getTenantSetting(
            TenantSettings.RegularShiftDifferentialEnd,
        );

        const contractsCanEnforceProjectManagers = (this.getTenantSetting(TenantSettings.ContractsCanEnforceProjectManagers)
            ?? 'false') === 'true';

        const enableEstimate = (this.getTenantSetting(
            TenantSettings.EnableEstimate,
        ) ?? 'false') === 'true';

        const enableContractCreation = (this.getTenantSetting(
            TenantSettings.EnableContractCreation,
        ) ?? 'false') === 'true';

        const useContractProjects = parseInt(
            this.getTenantSetting(TenantSettings.UseContractProjects)
            ?? -1,
        );
        const timesheetFlagEnabled = (this.getTenantSetting(TenantSettings.TimesheetFlagEnabled)
            ?? 'false') === 'true';

        const displayJobTypeInTimesheetManagement = (this.getTenantSetting(TenantSettings.DisplayJobTypeInTimesheetManagement)
            ?? 'false') === 'true';

        const driverPaySourcesEnabled = (this.getTenantSetting(TenantSettings.EnableDriverPaySources)
            ?? 'false') === 'true';

        const retainClientSignatureOnUpdate = (this.getTenantSetting(TenantSettings.RetainClientSignatureOnUpdate) ?? 'false') === 'true';

        const enablePrevailingWageCountyGroups = (this.getTenantSetting(
            TenantSettings.EnablePrevailingWageCountyGroups,
        ) ?? 'false') === 'true';

        const enablePremiumPayroll = (this.getTenantSetting(
            TenantSettings.EnablePremiumPayroll,
        ) ?? 'false') === 'true';

        const enableWeekendShiftDifferential = (this.getTenantSetting(
            TenantSettings.EnableWeekendShiftDifferential,
        ) ?? 'false') === 'true';
        const enableWAHR = (this.getTenantSetting(TenantSettings.EnableWAHR) ?? 'false') === 'true';

        // recently added settings configuration
        const employeeImportSwitchEnabled = (this.getTenantSetting(TenantSettings.EmployeeImportSwitchEnabled) ?? 'false') === 'true';
        const hasHoursReport = (this.getTenantSetting(TenantSettings.HasHoursReport) ?? 'false') === 'true';
        const jobAssignmentCancellationSMSTemplate = this.getTenantSetting(TenantSettings.JobAssignmentCancellationSMSTemplate) ?? '';
        const jobAssignmentClientCancellationSMSTemplate = this.getTenantSetting(TenantSettings.JobAssignmentClientCancellationSMSTemplate) ?? '';
        const jobAssignmentOverrideSMSTemplate = this.getTenantSetting(TenantSettings.JobAssignmentOverrideSMSTemplate) ?? '';
        const jobAssignmentSMSTemplate = this.getTenantSetting(TenantSettings.JobAssignmentSMSTemplate) ?? '';
        const jobTitleTemplate = this.getTenantSetting(TenantSettings.JobTitleTemplate) ?? '';
        const timesheetRejectSMSTemplate = this.getTenantSetting(TenantSettings.TimesheetRejectSMSTemplate) ?? '';
        const useQuickBooksInvoicing = (this.getTenantSetting(TenantSettings.UseQuickBooksInvoicing) ?? 'false') === 'true';
        const timeZone = this.getTenantSetting(TenantSettings.TimeZone) ?? '';
        const enablePreJobForm = (this.getTenantSetting(TenantSettings.EnablePreJobForm) ?? 'false') === 'true';
        const printForemanOnInvoiceDetails = (this.getTenantSetting(TenantSettings.PrintForemanOnInvoiceDetails) ?? 'false') === 'true';
        const showWorkCardOnTimesheet = (this.getTenantSetting(TenantSettings.ShowWorkCardOnTimesheet)
            ?? 'false') === 'true';
        const enableWorkCardValidation = (this.getTenantSetting(TenantSettings.EnableWorkCardValidation)
            ?? 'false') === 'true';
        const hideInvoicePrefixOnPrint = (this.getTenantSetting(TenantSettings.HideInvoicePrefixOnPrint) ?? 'false') === 'true';
        const disableJobOverrideTimeRestriction = (this.getTenantSetting(TenantSettings.DisableJobOverrideTimeRestriction)
            ?? 'false') === 'true';
        const enableUnion40OTBilling = (this.getTenantSetting(TenantSettings.EnableUnion40OTBilling)
            ?? 'false') === 'true';
        const exemptLateArrivalsFromBumpRules = (this.getTenantSetting(
            TenantSettings.ExemptLateArrivalsFromBumpRules,
        ) ?? 'false') === 'true';
        const enableShiftDifferentialBilling = (this.getTenantSetting(TenantSettings.EnableShiftDifferentialBilling)
            ?? 'false') === 'true';
        const enableEquipmentTags = (this.getTenantSetting(TenantSettings.EnableEquipmentTags) ?? 'false') === 'true';
        const enableBillPeriods = (this.getTenantSetting(TenantSettings.EnableBillPeriods) ?? 'false') === 'true';
        const enablePremiumBilling = (this.getTenantSetting(TenantSettings.EnablePremiumBilling) ?? 'false') === 'true';
        const enableBillingOT2 = (this.getTenantSetting(TenantSettings.EnableBillingOT2) ?? 'false') === 'true';

        const urlSegment = this.getTenantSetting(TenantSettings.UrlSegment);

        const shiftDifferentialPayAdjustment = parseFloat(this.getTenantSetting(TenantSettings.ShiftDifferentialPayAdjustment) ?? '0.0m');

        const showTimesheetStatusHistory = (this.getTenantSetting(TenantSettings.ShowTimesheetStatusHistory) ?? 'false') === 'true';

        const setInvoiceDateBasedOnLastTimesheet = (this.getTenantSetting(TenantSettings.SetInvoiceDateBasedOnLastTimesheet) ?? 'false') === 'true';
        const enableQuickSales = (this.getTenantSetting(TenantSettings.EnableQuickSales) ?? 'false') === 'true';
        const quickRentalContractTemplateId = this.getTenantSetting(TenantSettings.QuickRentalContractTemplateId)
            ? parseInt(this.getTenantSetting(TenantSettings.QuickRentalContractTemplateId), 10)
            : null;
        const combineRentalNonRentalChargesOnRentalInvoices = (this.getTenantSetting(TenantSettings.CombineRentalNonRentalChargesOnRentalInvoices) ?? 'false') === 'true';
        const enableMonthEndRentalInvoice = (this.getTenantSetting(TenantSettings.EnableMonthEndRentalInvoice) ?? 'false') === 'true';
        const enableSundayOT = (this.getTenantSetting(TenantSettings.EnableSundayOT) ?? 'false') === 'true';
        const enableHolidayOT = (this.getTenantSetting(TenantSettings.EnableHolidayOT) ?? 'false') === 'true';
        const useRadioStyleDaySelection = (this.getTenantSetting(TenantSettings.UseRadioStyleDaySelection) ?? 'false') === 'true';

        const enablePayrollStatusInChargeView = (this.getTenantSetting(TenantSettings.EnablePayrollStatusInChargeView) ?? 'false') === 'true';
        const remittanceAddressOnPrintedInvoice = (this.getTenantSetting(TenantSettings.RemittanceAddressOnPrintedInvoice) ?? '').replace(/\\n/g, '\n');
        const footerNotesOnPrintedInvoice = (this.getTenantSetting(TenantSettings.FooterNotesOnPrintedInvoice) ?? '').replace(/\\n/g, '\n');

        const enablePayIncentivesforCertifications = (this.getTenantSetting(TenantSettings.EnablePayIncentivesforCertifications) ?? 'false') === 'true';
        const applyDTtoFullShift = (this.getTenantSetting(TenantSettings.ApplyDTtoFullShift) ?? 'false') === 'true';
        const requireResourceKitOnJobBuild = (this.getTenantSetting(TenantSettings.RequireResourceKitOnJobBuild) ?? 'false') === 'true';


        //Certified Payroll
        const payrollAppTypeId = this.getTenantSetting(TenantSettings.PayrollAppTypeId);
        const payrollAppId = this.getTenantSetting(TenantSettings.PayrollAppId);
        const payrollAppApiKey = this.getTenantSetting(TenantSettings.PayrollAppApiKey);
        const payrollAppApiSecret = this.getTenantSetting(TenantSettings.PayrollAppApiSecret);
        const certifiedPayrollReporterName = this.getTenantSetting(TenantSettings.CertifiedPayrollReporterName);
        const certifiedPayrollReporterTitle = this.getTenantSetting(TenantSettings.CertifiedPayrollReporterTitle);
        const enableContractTimesheetCheckins = (this.getTenantSetting(TenantSettings.EnableContractTimesheetCheckins)
            ?? 'false') === 'true';

        const enablePaintBatchCodes = (this.getTenantSetting(TenantSettings.EnablePaintBatchCodes)
            ?? 'false') === 'true';

        const enableContractJobTypeBillingSetting = (this.getTenantSetting(TenantSettings.EnableContractJobTypeBillingSetting)
            ?? 'false') === 'true';

        const fringePaidInCash = (this.getTenantSetting(TenantSettings.FringePaidInCash) ?? 'false') === 'true';
        const includeMedicareDeductionsInFICAColumn = (this.getTenantSetting(TenantSettings.IncludeMedicareDeductionsInFICAColumn) ?? 'false') === 'true';

        //NetSuite Settings
        const netSuiteInvoiceForm = this.getTenantSetting(TenantSettings.NetSuiteInvoiceForm);
        const netSuiteICInvoiceForm = this.getTenantSetting(TenantSettings.NetSuiteICInvoiceForm);
        const netSuiteICEndInvoiceForm = this.getTenantSetting(TenantSettings.NetSuiteICEndInvoiceForm);
        const netSuiteICOffice = this.getTenantSetting(TenantSettings.NetSuiteICOffice);
        const netSuiteDescriptionItem = this.getTenantSetting(TenantSettings.NetSuiteDescriptionItem);
        const netSuiteICDescriptionItem = this.getTenantSetting(TenantSettings.NetSuiteICDescriptionItem);
        const netSuitePercentageItem = this.getTenantSetting(TenantSettings.NetSuitePercentageItem);
        const netSuiteMultiSubcontractor = this.getTenantSetting(TenantSettings.NetSuiteMultiSubcontractor);
        const netSuiteInvoiceCustomerPickup = (this.getTenantSetting(TenantSettings.NetSuiteInvoiceCustomerPickup) ?? 'false') === 'true';
        const allowTimesheetAttachments = (this.getTenantSetting(TenantSettings.AllowTimesheetAttachments) ?? 'false') === 'true';
        const mapId = this.getTenantSetting(TenantSettings.MapId);
        const includeEquipmentResourceNameInInvoice = (this.getTenantSetting(TenantSettings.IncludeEquipmentResourceNameInInvoice) ?? 'false') === 'true';
        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, tenant)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="tenantForm"
                    formHeadingIcon={faWarehouse}
                    formHeading={`Tenant Settings:  ${companyName}`}
                    formName="tenantForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    onClearErrors={this.onClearErrors}
                    loading={this.state.loading}
                    backUrl={AppNavPaths.Tenants}
                    backLabel="Back to Tenants"
                >
                    <Row>
                        <Col
                            xs={12}
                            md={5}
                        >
                            <div className="table-of-contents">
                                <ListGroup>
                                    <ListGroupItem
                                        hash="brandapp"
                                        activeHash={activeHash}
                                    >
                                        Logo/Site Options
                                    </ListGroupItem>

                                    <ListGroupItem
                                        hash="billing"
                                        activeHash={activeHash}
                                    >
                                        Billing & Invoicing
                                    </ListGroupItem>

                                    <ListGroupItem
                                        hash="contracts"
                                        activeHash={activeHash}
                                    >
                                        Contracts & Organizations
                                    </ListGroupItem>

                                    <ListGroupItem
                                        hash="resources"
                                        activeHash={activeHash}
                                    >
                                        Employees & Equipment
                                    </ListGroupItem>

                                    <ListGroupItem
                                        hash="jobs"
                                        activeHash={activeHash}
                                    >
                                        Jobs & Dispatching
                                    </ListGroupItem>

                                    <ListGroupItem
                                        hash="netsuite"
                                        activeHash={activeHash}
                                    >
                                        Netsuite
                                    </ListGroupItem>

                                    <ListGroupItem
                                        hash="payroll"
                                        activeHash={activeHash}
                                    >
                                        Payroll
                                    </ListGroupItem>

                                    <ListGroupItem
                                        hash="reporting"
                                        activeHash={activeHash}
                                    >
                                        Reporting
                                    </ListGroupItem>
                                </ListGroup>

                                <BlockBorder />

                                <SaveButton
                                    name="tenantForm"
                                    type="button"
                                    color="success"
                                    label="Save Settings"
                                    onClick={this.onSubmit}
                                    saving={this.state.saving}
                                    className="w-100"
                                />
                            </div>
                        </Col>

                        <Col
                            xs={12}
                            md={7}
                        >
                            <SubHeading
                                id="brandapp"
                                onIntersect={this.handleIntersection}
                            >
                                Brand/App Logo
                            </SubHeading>

                            {/* 2022-08-04 - M. Nicol - Logo dimensions set in /style/App.scss .navbar-brand-image
                                *  Also see NavMenu.js. */}

                            <List>
                                <li>
                                    <strong>
                                        Save button below does not apply to this
                                        section.
                                    </strong>
                                </li>
                                <li>
                                    You must reload or log out/back in to see
                                    new logo images.
                                </li>
                            </List>

                            <FormGroup>
                                <FlexRow>
                                    <FlexColumnCenter className="col-sm-12 col-md-6">
                                        <FileUpload
                                            id="brandLogoUpoader"
                                            ref={this.brandLogoUploadsRef}
                                            accept={supportedFiles}
                                            maxNameLength={150}
                                            url={ApiRoutes.tenant.updateBrandLogo(
                                                tenant.id,
                                                this.state.useAsAppLogo,
                                            )}
                                            onUploadComplete={() => null}
                                        />
                                    </FlexColumnCenter>
                                    <FlexColumnCenter className="col-sm-12 col-md-6">
                                        <div className="pl-3">
                                            <img
                                                src={ApiRoutes.tenant.getTenantLogo(this.props.match.params.id)}
                                                width="125"
                                                alt="Brand logo"
                                            />

                                            <p className="pl-3 text-center">
                                                Current Logo
                                            </p>
                                        </div>
                                    </FlexColumnCenter>
                                </FlexRow>

                                <FormCheckbox

                                    id="chkLogoAsAppLogo"
                                    checked={useAsAppLogo}
                                    onChange={(e) => this.setState({
                                        useAsAppLogo: e.target.checked,
                                    })}
                                    labelText="Use as App Logo (login page logo)"
                                />
                            </FormGroup>
                            <SubHeading>Report Logo</SubHeading>
                            <FormGroup>
                                <List>
                                    <li>
                                        <strong>
                                            Save button below does not apply to
                                            this section.
                                        </strong>
                                    </li>
                                    <li>
                                        Image must be 50px tall. Width can be
                                        variable to an extent.
                                    </li>
                                </List>

                                <FlexRow>
                                    <FlexColumnCenter className="col-sm-12 col-md-6">
                                        <FileUpload
                                            id="reportLogoUpoader"
                                            ref={this.reportLogoUploadsRef}
                                            accept={supportedFiles}
                                            maxNameLength={150}
                                            url={ApiRoutes.tenant.updateReportLogo(
                                                this.props.match.params.id,
                                            )}
                                        />
                                    </FlexColumnCenter>
                                    <FlexColumnCenter className="col-sm-12 col-md-6">
                                        <div className="pl-3">
                                            <img
                                                src={ApiRoutes.tenant.getReportLogo(this.props.match.params.id)}
                                                width="125"
                                                alt="Report logo"
                                            />

                                            <p className="pl-3 text-center">
                                                Report Logo
                                            </p>
                                        </div>
                                    </FlexColumnCenter>
                                </FlexRow>
                            </FormGroup>

                            <SubHeading>Site</SubHeading>
                            <Block component={FormGroup}>
                                <Input
                                    label="URL Segment"
                                    id="urlSegment"
                                    name="urlSegment"
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.UrlSegment,
                                        event.target.value,
                                    )}
                                    value={urlSegment}
                                    placeholder="Enter a url segment"
                                />

                                <UncontrolledTooltip
                                    placement="left"
                                    target="urlSegment"
                                >
                                    The segment/subdomain that appears in the url - e.g. alpha.roads.righttraffic.com, sws.roads.righttraffic.com
                                </UncontrolledTooltip>

                                <Input
                                    label="Banner Message"
                                    id="bannerMessage"
                                    name="bannerMessage"
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.BannerMessage,
                                        event.target.value,
                                    )}
                                    value={bannerMessage}
                                    placeholder="Optional Banner Message"
                                />
                                <UncontrolledTooltip
                                    placement="left"
                                    target="bannerMessage"
                                >
                                    This message appears in the main nav banner at the top of the screen
                                </UncontrolledTooltip>

                                <FormCheckbox
                                    id="chkBannerMessageExternallyVisible"
                                    checked={bannerMessageExternallyVisible}
                                    className="mt-2"
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.BannerMessageExternallyVisible,
                                        event.target.checked.toString(),
                                    )}
                                    labelText="Show Banner Message To Unauthenticated Users"
                                />

                                <UncontrolledTooltip
                                    placement="left"
                                    target="chkBannerMessageExternallyVisible"
                                >
                                    Causes the banner message to appear on the login page
                                </UncontrolledTooltip>
                            </Block>

                            <InfoCheckbox
                                id="QuirksEnabled"
                                checked={quirksEnabled}
                                label="This customer wants changes rolled out slowly"
                                tooltip="Disables certain features and enhancements for this customer in case they want these improvements to be rolled out slowly."
                                badge={FeatureBadge}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.QuirksEnabled,
                                    event.target.checked.toString(),
                                )}
                            />

                            <ValidatedSelect
                                id="timezone"
                                name="timezone"
                                label="Tenant Time Zone"
                                options={timezones?.winTimezones ?? []}
                                value={
                                    timezones?.winTimezones?.find(
                                        (s) => s.id === (timezones.ianaTimezones?.find((z) => z.value === timeZone)?.id ?? timeZone),
                                    )
                                    ?? ''
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.TimeZone,
                                    event?.value || '',
                                )}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="timezone"
                            >
                                Select the time zone for this tenant.
                            </UncontrolledTooltip>

                            <SubHeading>Localization</SubHeading>

                            <Block component={FormGroup}>
                                <ValidatedSelect
                                    id="supportedLanguages"
                                    name="supportedLanguages"
                                    label="Supported Languages"
                                    isMulti
                                    options={(availableLanguages ?? []).map((lng) => ({
                                        value: lng.id,
                                        label: LANGUAGE_LABELS[lng.name] || lng.description || lng.name,
                                        isFixed: lng.name === DEFAULT_LANGUAGE,
                                    }))}
                                    value={(availableLanguages ?? [])
                                        .filter((lng) => tenantLanguageIds.includes(lng.id))
                                        .map((lng) => ({
                                            value: lng.id,
                                            label: LANGUAGE_LABELS[lng.name] || lng.description || lng.name,
                                            isFixed: lng.name === DEFAULT_LANGUAGE,
                                        }))}
                                    onChange={(selected) => {
                                        const englishId = (availableLanguages ?? []).find((l) => l.name === DEFAULT_LANGUAGE)?.id;
                                        const ids = (selected ?? []).map((s) => s.value);
                                        if (englishId && !ids.includes(englishId)) ids.push(englishId);
                                        this.setState({ tenantLanguageIds: ids });
                                    }}
                                />
                                <UncontrolledTooltip
                                    placement="left"
                                    target="supportedLanguages"
                                >
                                    Languages staff can choose from. English is required.
                                </UncontrolledTooltip>
                            </Block>

                            <SubHeading
                                id="billing"
                                onIntersect={this.handleIntersection}
                            >
                                Billing
                            </SubHeading>

                            <InfoCheckbox
                                id="chkBillingEnabled"
                                checked={billingEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.BillingEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Billing"
                                tooltip="Adds billing support to the platform, which is required for payroll and invoicing"
                                badge={FeatureBadge}
                            >
                                <InfoCheckbox
                                    id="chkEnableDoubleTimeBilling"
                                    checked={enableDoubleTimeBilling}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableDoubleTimeBilling,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Double-Time Billing"
                                    tooltip="Hours worked on Sundays and Holidays will be considered double-time (DT) hours for contracts that have DT rates on them. All union contracts have DT enabled automatically. Otherwise, this adds an organization setting."
                                    color="dark"
                                    badge={SettingBadge}
                                />

                                <InfoCheckbox
                                    id="chkEnableBillPeriods"
                                    checked={enableBillPeriods}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableBillPeriods,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Weekly OT Billing"
                                    tooltip="Turns on OT tracking for the selected tenant and enables billing OT for passing weekly thresholds."
                                    badge={SettingBadge}
                                    color="dark"
                                />

                                <InfoCheckbox
                                    id="chkEnableBillingOT2"
                                    checked={enableBillingOT2}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableBillingOT2,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable OT2"
                                    tooltip="Apply customer daily OT on hours scheduled as OT."
                                    badge={SettingBadge}
                                    color="dark"
                                />

                                <InfoCheckbox
                                    id="chkPremiumBilling"
                                    checked={enablePremiumBilling}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnablePremiumBilling,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Premium Billing "
                                    tooltip="Hours worked on Sundays, Holidays and Emergency jobs will be considered premium-hours, overriding DT Billing logic."
                                    color="dark"
                                    badge={SettingBadge}
                                />

                                <InfoCheckbox
                                    id="chkShiftDifferentialBilling"
                                    checked={enableShiftDifferentialBilling}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableShiftDifferentialBilling,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Shift Differential Billing"
                                    tooltip="Adds support for Shift Differential billing hours to Customer organizations"
                                    color="dark"
                                    badge={SettingBadge}
                                />

                                <InfoCheckbox
                                    id="chkEnableAlternativePayrollBilling"
                                    checked={enableAlternativePayrollBilling}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableAlternativePayrollBilling,
                                        event.target.checked.toString(),
                                    )}
                                    label='Enable "Alternative" Billing and Payroll options'
                                    tooltip="Adds support for legacy &quot;Alternative&quot; billing and payroll organizations, including payroll PR hours."
                                    color="warning"
                                    badge={NewFieldBadge}
                                />

                                <InfoCheckbox
                                    id="chkAutoCalculateDriveTimeBilling"
                                    checked={autoCalculateDriveTimeBilling}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.AutoCalculateDriveTimeBilling,
                                        event.target.checked.toString(),
                                    )}
                                    label="Auto-Calculate Drive Time for Billing"
                                    tooltip="When enabled, drive time charges on billing are automatically calculated using Google Routes API based on the distance between the Dispatch Port and the Job Location."
                                    color="dark"
                                    badge={SettingBadge}
                                />
                            </InfoCheckbox>

                            <SubHeading>Invoicing</SubHeading>

                            <InfoCheckbox
                                id="chkInvoicingEnabled"
                                checked={invoicingEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.InvoicingEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Invoicing"
                                tooltip="Adds the Invoicing feature"
                                badge={FeatureBadge}
                            >
                                <InfoCheckbox
                                    id="chkUseQuickBooksInvoicing"
                                    checked={useQuickBooksInvoicing}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.UseQuickBooksInvoicing,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Inventory Items"
                                    tooltip="Adds support for inventory item tracking with Netsuite"
                                    badge={FeatureBadge}
                                />

                                <InfoCheckbox
                                    id="chkPrintForemanOnInvoiceDetails"
                                    checked={printForemanOnInvoiceDetails}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.PrintForemanOnInvoiceDetails,
                                        event.target.checked.toString(),
                                    )}
                                    label="Print Foreman On Invoice Details"
                                    tooltip="When enabled, the Foreman will be included on the printed invoice."
                                    badge={SettingBadge}
                                    color="dark"
                                />

                                <InfoCheckbox
                                    id="chkHideInvoicePrefixOnPrint"
                                    checked={hideInvoicePrefixOnPrint}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.HideInvoicePrefixOnPrint,
                                        event.target.checked.toString(),
                                    )}
                                    label="Hide Invoice Prefix On Print"
                                    tooltip="When enabled, Prefix in invoice number will be hidden on printed invoice."
                                    color="dark"
                                    badge={SettingBadge}
                                />
                                <InfoCheckbox
                                    id="chkRollUpDriveTime"
                                    checked={rollUpDriveTimeEnabled}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.RollUpDriveTime,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Drive Time Roll up on Billing"
                                    tooltip="Combines all drive time hours into one charge in billing and invoicing. The resultant charge will have a quantity of 1."
                                    color="dark"
                                    badge={SettingBadge}
                                />

                                <InfoCheckbox
                                    id="setInvoiceDateBasedOnLastTimesheet"
                                    checked={
                                        setInvoiceDateBasedOnLastTimesheet
                                    }
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.SetInvoiceDateBasedOnLastTimesheet,
                                        event.target.checked.toString(),
                                    )}
                                    label="Set Invoice Date Based on Last Timesheet"
                                    tooltip="Use Last Timesheet Date as Invoice Date."
                                    color="dark"
                                    badge={SettingBadge}
                                />

                                <InfoCheckbox
                                    id="chkCombineRentalNonRentalChargesOnRentalInvoices"
                                    checked={combineRentalNonRentalChargesOnRentalInvoices}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.CombineRentalNonRentalChargesOnRentalInvoices,
                                        event.target.checked.toString(),
                                    )}
                                    label="Combine Rental/Non-rental Charges on Rental Invoice"
                                    tooltip="Enables the inclusion of approved non-rental charges together with rental charges on rental invoices, allowing them to be billed in a single combined invoice."
                                    color="dark"
                                    badge={SettingBadge}
                                />

                                <InfoCheckbox
                                    id="chkEnableMonthEndRentalInvoice"
                                    checked={enableMonthEndRentalInvoice}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableMonthEndRentalInvoice,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Month End Rental Invoice"
                                    tooltip="Auto generate a month end rental invoice on the first day of each month."
                                    color="dark"
                                    badge={SettingBadge}
                                />

                                <InfoCheckbox
                                    className="mt-2 mb-2"
                                    id="chkEnableQuickSales"
                                    checked={enableQuickSales}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableQuickSales,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Quick Sales"
                                    tooltip="Allow the creation and invoicing of quick sales"
                                    badge={FeatureBadge}
                                />

                                <InfoCheckbox
                                    className="mt-2 mb-2"
                                    id="chkIncludeEquipmentResourceNameInInvoice"
                                    checked={includeEquipmentResourceNameInInvoice}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.IncludeEquipmentResourceNameInInvoice,
                                        event.target.checked.toString(),
                                    )}
                                    label="Include Equipment Resource Name in Invoice Descriptions"
                                    tooltip="Include the resource name in the invoice descriptions"
                                    badge={FeatureBadge}
                                />

                                <FormGroup>
                                    <Textarea
                                        label="Printed Invoice Remittance Address"
                                        id="remittanceAddressOnPrintedInvoice"
                                        name="remittanceAddressOnPrintedInvoice"
                                        rows="5"
                                        onKeyDown={onKeyDown}
                                        onChange={this.remittanceAddressOnChange}
                                        value={remittanceAddressOnPrintedInvoice}
                                    />
                                    <UncontrolledTooltip
                                        placement="left"
                                        target="remittanceAddressOnPrintedInvoice"
                                    >
                                        Tip: For multi-line addresses, use Shift + Enter (up to 5 lines allowed, 50 char per line).
                                    </UncontrolledTooltip>
                                </FormGroup>

                                <FormGroup>
                                    <Textarea
                                        label="Printed Invoice Footer Note"
                                        id="footerNotesOnPrintedInvoice"
                                        name="footerNotesOnPrintedInvoice"
                                        rows="8"
                                        cleanText={false}
                                        onChange={this.footerNotesOnChange}
                                        value={footerNotesOnPrintedInvoice}
                                    />
                                    <UncontrolledTooltip
                                        placement="left"
                                        target="footerNotesOnPrintedInvoice"
                                    >
                                        Tip: Prints above the remittance address on the invoice footer. Use Shift + Enter for line breaks, and
                                        &lt;b&gt;, &lt;i&gt;, or &lt;u&gt; tags for bold, italic, or underline (e.g. &lt;b&gt;Payment Options&lt;/b&gt;).
                                    </UncontrolledTooltip>
                                </FormGroup>
                            </InfoCheckbox>

                            <SubHeading>Charge Types</SubHeading>

                            <InfoCheckbox
                                id="chkAllowDefaultChargeTypeValues"
                                checked={allowDefaultChargeTypeValues}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.AllowDefaultChargeTypeValues,
                                    event.target.checked.toString(),
                                )}
                                label="Allow default values for charge types to be configured?"
                                tooltip="Adds support for setting default timesheet values for flat rate charge types"
                                badge={NewFieldBadge}
                            />

                            <InfoCheckbox
                                id="chkBillingRatesEnabled"
                                checked={billingRatesEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.BillingRatesEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Billing Rates"
                                tooltip="Allows customizing rates for charge types on a per-customer or per-organization level"
                                badge={FeatureBadge}
                            />

                            <InfoCheckbox
                                id="chkUseLimitedApprovals"
                                checked={useLimitedApprovals}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.UseLimitedApprovals,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Limited Approvals"
                                tooltip="Adds a field to the Charge Type that allows restricting timesheet approvals to specific user groups"
                                color="warning"
                                badge={NewFieldBadge}
                            />

                            <InfoCheckbox
                                id="chkBlockBillingCancelEnabled"
                                checked={blockBillingCancelEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.BlockBillingCancelEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Show Block Cancellation if Charge > 0 for Billing"
                                tooltip="Adds a field to the Charge Type form that allows for blocking job cancellation if the charge is on it"
                                color="warning"
                                badge={NewFieldBadge}
                            />

                            <InfoCheckbox
                                id="chkBlockPayrollCancelEnabled"
                                checked={blockPayrollCancelEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.BlockPayrollCancelEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Show Block Cancellation if Charge > 0 for Payroll"
                                tooltip="Adds a field to the Charge Type form that allows for blocking payroll cancellation if the charge is on the associated job"
                                color="warning"
                                badge={NewFieldBadge}
                            />

                            <InfoCheckbox
                                id="chkCaptureStateMinWage"
                                checked={useStateMinimum}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.UseStateMinimum,
                                    event.target.checked.toString(),
                                )}
                                label="Capture State Minimum Wage"
                                tooltip="Adds a field to the Charge Type form that specifies if it is to be paid at the state minimum rate"
                                color="warning"
                                badge={NewFieldBadge}
                            />


                            <InfoCheckbox
                                id="chkAllowRentalChargeTypes"
                                checked={allowRentalChargeTypes}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.AllowRentalChargeTypes,
                                    event.target.checked.toString(),
                                )}
                                label="Allow rentals and rental charge types"
                                tooltip="Adds support for rental equipment and rental charge types."
                                badge={FeatureBadge}
                            >
                                <Input
                                    label="Rental Invoices Billing End Date"
                                    id="billingPeriodEndDate"
                                    type="date"
                                    name="billingPeriodEndDate"
                                    value={billingPeriodEndDate ?? ''}
                                    onChange={(e) => this.setState({
                                        billingPeriodEndDate: e.target.value,
                                    })}
                                />

                                <Button
                                    disabled={!!this.state.saving}
                                    color="primary"
                                    name="rentalInvoices"
                                    onClick={this.createRentalInvoices}
                                    outline
                                >
                                    {this.state.saving ? 'Creating Rental Invoices...' : 'Create Rental Invoices'}
                                </Button>
                            </InfoCheckbox>

                            <InfoCheckbox
                                id="chkChargeTypeGroupsEnabled"
                                checked={chargeTypeGroupsEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.ChargeTypeGroupsEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Charge Type Groups"
                                tooltip="Adds the Charge Type Groups feature, which allows multiple charge types and quantities of charge types to be grouped into one line item"
                                badge={FeatureBadge}
                            />

                            <SubHeading
                                id="contracts"
                                onIntersect={this.handleIntersection}
                            >
                                Contracts
                            </SubHeading>

                            <ValidatedSelect
                                id="useContractProjects"
                                name="useContractProjects"
                                label="Use Contract Projects"
                                required
                                options={contractProjectsOptions}
                                value={contractProjectsOptions.find(
                                    (x) => x.id === useContractProjects,
                                )}
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.name}
                                onChange={(item) => this.onSettingChanged(
                                    TenantSettings.UseContractProjects,
                                    (item ?? {}).id ?? '',
                                )}
                                isClearable={false}
                                error="Using Contract Projects required."
                            />

                            <ValidatedSelect
                                id="quickRentalContractTemplateId"
                                name="quickRentalContractTemplateId"
                                label="Default Quick Rental Contract Template"
                                options={quickRentalContractTemplates}
                                value={(quickRentalContractTemplates ?? []).find(
                                    (x) => x.value === quickRentalContractTemplateId,
                                ) ?? null}
                                getOptionValue={(option) => option.value}
                                getOptionLabel={(option) => option.label}
                                onChange={(item) => this.onSettingChanged(
                                    TenantSettings.QuickRentalContractTemplateId,
                                    item ? item.value.toString() : '',
                                )}
                                isClearable
                            />

                            <ValidatedSelect
                                id="contractScheduleTypes"
                                name="tenant.contractScheduleTypes"
                                label="Optional Contract Schedule(s)"
                                isMulti
                                options={optionalContractScheduleTypes}
                                value={
                                    (optionalContractScheduleTypes ?? []).filter(
                                        (x) => (
                                            tenant.contractScheduleTypes ?? []
                                        ).includes(x.value),
                                    ) ?? ''
                                }
                                onChange={this.onSelectChange}
                            />

                            <InfoCheckbox
                                id="chkEnableContractCreation"
                                checked={enableContractCreation}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableContractCreation,
                                    event.target.checked.toString(),
                                )}
                                badge={SettingBadge}
                                label="Enable creating contracts from contract list view"
                                tooltip="Allow adding contracts from contract view"
                                color="dark"
                            >

                            </InfoCheckbox>

                            <InfoCheckbox
                                id="chkContractUnionWork"
                                checked={enforceContractUnionWork}
                                disabled={enablePrevailingWageContracts}
                                onChange={(event) => {
                                    const isChecked = event.target.checked;
                                    this.onSettingChanged(
                                        TenantSettings.EnforceContractUnionWork,
                                        isChecked.toString(),
                                    );
                                    if (!isChecked) {
                                        this.onSettingChanged(
                                            TenantSettings.EnableUnion40OTBilling,
                                            'false',
                                        );
                                        this.onSettingChanged(
                                            TenantSettings.ExemptLateArrivalsFromBumpRules,
                                            'false',
                                        );
                                    }
                                }}
                                badge={FeatureBadge}
                                label="Add support for Union work"
                                tooltip="Contracts must select one of the union work type options when being created - one of None, Distribution, or Heavy Highway"
                            >
                                <InfoCheckbox
                                    id="chkUnion40OTBilling"
                                    checked={enableUnion40OTBilling}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableUnion40OTBilling,
                                        event.target.checked.toString(),
                                    )}
                                    formGroupClassName="mb-0"
                                    color="dark"
                                    badge={SettingBadge}
                                    label="Union work >40hr/wk bills as OT"
                                    tooltip="Determines whether or not union work bills as OT over 40hr. This option does not affect payroll, which always pays OT over 40hr."
                                />
                                <InfoCheckbox
                                    id="chkExemptLateArrivalsFromBumpRules"
                                    checked={exemptLateArrivalsFromBumpRules}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.ExemptLateArrivalsFromBumpRules,
                                        event.target.checked.toString(),
                                    )}
                                    formGroupClassName="mb-0 mt-2"
                                    color="dark"
                                    badge={SettingBadge}
                                    label="Employees don't get bumped with late arrival or early departure"
                                    tooltip="When enabled, employees with an undismissed late arrival or early departure flag on a union timesheet are excluded from bump rules in both billing and payroll."
                                />
                            </InfoCheckbox>

                            <InfoCheckbox
                                id="EnablePrevailingWageContracts"
                                checked={enablePrevailingWageContracts}
                                disabled={!!enforceContractUnionWork}
                                onChange={(event) => {
                                    const isChecked = event.target.checked;
                                    this.onSettingChanged(
                                        TenantSettings.EnablePrevailingWageContracts,
                                        isChecked.toString(),
                                    );
                                    if (!isChecked) {
                                        this.onSettingChanged(
                                            TenantSettings.EnablePrevailingWageCountyGroups,
                                            'false',
                                        );
                                    }
                                }}
                                badge={FeatureBadge}
                                label="Enable Prevailing Wages"
                                tooltip="Add a field to contracts to enable prevailing wages and enables access to other features, such as county capture"
                            >
                                <InfoCheckbox
                                    id="EnablePrevailingWageCountyGroups"
                                    checked={enablePrevailingWageCountyGroups}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnablePrevailingWageCountyGroups,
                                        event.target.checked.toString(),
                                    )}
                                    formGroupClassName="mb-0"
                                    color="dark"
                                    badge={SettingBadge}
                                    label="Use County Groups instead of Pay Schedules for prevailing wage"
                                    tooltip="When enabled, County Groups will be used instead of Pay Schedules for prevailing wage work."
                                />
                            </InfoCheckbox>
                            <InfoCheckbox
                                id="EnableContractJobTypeBillingSetting"
                                checked={enableContractJobTypeBillingSetting}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableContractJobTypeBillingSetting,
                                    event.target.checked.toString(),
                                )}
                                badge={FeatureBadge}
                                label="Allow Custom Billing Rates per Job Type"
                                tooltip="Allows setting custom billing rates per job type within a contract."
                            />

                            <InfoCheckbox
                                id="GLStringsEnabled"
                                checked={gLStringsEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.GLStringsEnabled,
                                    event.target.checked.toString(),
                                )}
                                color="warning"
                                badge={NewFieldBadge}
                                label="Enable GL Strings"
                                tooltip="Allows setting contracts to require a GL (general ledger) string, which appears on invoices"
                            />

                            <InfoCheckbox
                                id="chkContractsCanEnforceProjectManagers"
                                checked={contractsCanEnforceProjectManagers}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.ContractsCanEnforceProjectManagers,
                                    event.target.checked.toString(),
                                )}
                                color="warning"
                                badge={NewFieldBadge}
                                label="Contracts can enforce Project Managers"
                                tooltip="Allows Project Managers to be added to contract jobs."
                            />

                            <InfoCheckbox
                                id="ShowWorkCardOnTimesheet"
                                checked={showWorkCardOnTimesheet}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.ShowWorkCardOnTimesheet,
                                    event.target.checked.toString(),
                                )}
                                color="warning"
                                badge={NewFieldBadge}
                                label="Enable Work Card on Timesheet"
                                tooltip="Allows contracts to require a Work Card attachment, which will be displayed on the Timesheet."
                            />

                            <InfoCheckbox
                                id="EnableWorkCardValidation"
                                checked={enableWorkCardValidation}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableWorkCardValidation,
                                    event.target.checked.toString(),
                                )}
                                color="warning"
                                badge={NewFieldBadge}
                                label="Enable Work Card Validation"
                                tooltip="Allows work cards to be validated for legibility on upload"
                            />

                            <InfoCheckbox
                                id="enableContractTimesheetCheckins"
                                checked={enableContractTimesheetCheckins}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableContractTimesheetCheckins,
                                    event.target.checked.toString(),
                                )}
                                badge={NewFieldBadge}
                                color="warning"
                                label="Enable Contract Timesheet Check-ins"
                                tooltip='Adds a checkbox to the Contract form titled "Enable Timesheet Check-ins", and shows employee last event column in the Roster View table.'
                            />

                            <SubHeading>Organization</SubHeading>

                            <InfoCheckbox
                                id="chkEnableAlternateBillTo"
                                checked={enableAlternateBillTo}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableAlternateBillTo,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Alternate Bill To"
                                tooltip="Adds Billing Address fields to the Organization form, which show up in invoices"
                                badge={NewFieldBadge}
                                color="warning"
                            />

                            <ValidatedSelect
                                id="companyTypes"
                                name="tenant.companyTypes"
                                label="Optional Company Type(s)"
                                isMulti
                                options={optionalCompanyTypes}
                                value={
                                    (optionalCompanyTypes ?? []).filter(
                                        (x) => (
                                            tenant.companyTypes ?? []
                                        ).includes(x.value),
                                    ) ?? ''
                                }
                                onChange={this.onSelectChange}
                                error="The company type is required."
                            />

                            <SubHeading>Contacts</SubHeading>

                            <InfoCheckbox
                                id="chkContactScreenEnabled"
                                checked={contactScreenEnabled}
                                onChange={(event) => {
                                    const contactScreenEnabled = event.target.checked.toString();

                                    this.onSettingChanged(
                                        TenantSettings.ContactScreenEnabled,
                                        contactScreenEnabled,
                                    );

                                    if (contactScreenEnabled !== 'true') {
                                        this.onSettingChanged(
                                            TenantSettings.ContactActivitiesEnabled,
                                            'false',
                                        );
                                    }
                                }}
                                label="Enable Contacts Screen"
                                tooltip="Adds the Contacts dashboard, which allows for viewing and searching organization contacts without needing to navigate through organizations"
                                badge={FeatureBadge}
                            >
                                <InfoCheckbox
                                    id="chkContactActivitiesEnabled"
                                    checked={contactActivitiesEnabled}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.ContactActivitiesEnabled,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Contact Activities"
                                    tooltip="Adds the Contact Activities dashboard, which allows adding calendar events and other notes to contacts"
                                    badge={FeatureBadge}
                                />

                                <InfoCheckbox
                                    id="chkConstrainContactTypesEnabled"
                                    checked={constrainContactTypesEnabled}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.ConstrainContactTypesOnJobEnabled,
                                        event.target.checked.toString(),
                                    )}
                                    label="Constrain Contact Types on Jobs"
                                    tooltip="Only allow e.g. Foremen to be assigned as Foremen, Project Managers as Project Managers, etc."
                                    color="dark"
                                    badge={SettingBadge}
                                />
                            </InfoCheckbox>

                            <SubHeading>Mapping</SubHeading>

                            <Input
                                label="Google Maps API Key"
                                id="googleMapsApiKey"
                                required={
                                    captureCounty == CountyCaptureType.AllTimesheets
                                    || captureCounty == CountyCaptureType.PrevailingWageTimesheets
                                    || captureCounty == CountyCaptureType.UnionTimesheets
                                }
                                name="googleMapsApiKey"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.GoogleMapsApiKey,
                                    event.target.value,
                                )}
                                value={googleMapsApiKey}
                                error="Google Maps API key is required for county capture"
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="googleMapsApiKey"
                            >
                                Required for county capture - used for performing a Places search when adding addresses
                            </UncontrolledTooltip>

                            <Input
                                label="Map Id"
                                id="mapId"
                                name="mapId"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.MapId,
                                    event.target.value,
                                )}
                                value={mapId}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="mapId"
                            >
                                Required for Employees Map: The Map ID is needed to load and style the Google Map on the Employee Index page.
                            </UncontrolledTooltip>

                            <ValidatedSelect
                                id="countyCaptureType"
                                name="countyCaptureType"
                                label="County Capture"
                                options={countyCaptureTypes}
                                value={(countyCaptureTypes ?? []).filter(
                                    (x) => x.value == captureCounty ?? [],
                                )}
                                onChange={(item) => this.onSettingChanged(
                                    TenantSettings.CaptureCounty,
                                    item.value,
                                )}
                                error="The county capture type is required."
                            />

                            <UncontrolledTooltip
                                placement="left"
                                target="countyCaptureType"
                            >
                                Automatically detects counties when adding meeting or job locations
                            </UncontrolledTooltip>

                            <FormGroup>
                                <Button
                                    disabled={!!this.state.saving}
                                    size="sm"
                                    type="button"
                                    color="secondary"
                                    onClick={this.updateTenantDispatchTimezones}
                                >
                                    <FontAwesomeIcon
                                        className="mr-2"
                                        icon={faMap}
                                    />
                                    {this.state.saving ? 'Updating...' : 'Update Dispatch Location Time Zones'}
                                </Button>
                            </FormGroup>
                            <FormGroup>
                                <Button
                                    disabled={!!this.state.saving}
                                    size="sm"
                                    type="button"
                                    color="secondary"
                                    onClick={this.updateTenantEmployeeTimezones}
                                >
                                    <FontAwesomeIcon
                                        className="mr-2"
                                        icon={faMap}
                                    />
                                    {this.state.saving ? 'Updating...' : 'Update Employee Location Time Zones'}
                                </Button>
                            </FormGroup>

                            <SubHeading
                                id="resources"
                                onIntersect={this.handleIntersection}
                            >
                                Employees & Equipment
                            </SubHeading>

                            <InfoCheckbox
                                id="chkEmployeeOnboardingEnabled"
                                checked={empOnboardingEnabled}
                                onChange={this.onEmpOnboardingCheckedChanged}
                                label="Enable Employee Onboarding"
                                tooltip="Enables the &quot;New Hire&quot; button in the Employee listing page"
                                badge={FeatureBadge}
                            >
                                <ValidatedSelect
                                    id="onboardingDefaultGroup"
                                    name="onboardingDefaultGroup"
                                    required={empOnboardingEnabled}
                                    label="Default Employee Group"
                                    options={employeeGroups}
                                    value={currentOnboardEmpGroup}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.groupName}
                                    onChange={(item) => this.onSettingChanged(
                                        TenantSettings.EmployeeOnboardingDefaultGroup,
                                        (item ?? {}).id ?? '',
                                    )}
                                    error="When enabling emp onboarding, a default employee group selection is required."
                                />

                                <UncontrolledTooltip
                                    placement="left"
                                    target="onboardingDefaultGroup"
                                >
                                    Default user group to place new hires in
                                </UncontrolledTooltip>
                            </InfoCheckbox>

                            <InfoCheckbox
                                id="chkactiveEmployeeListEnabled"
                                checked={activeEmployeeListEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.ActiveEmployeeListEnabled,
                                    event.target.checked.toString(),
                                )}
                                badge={FeatureBadge}
                                label="Enable Active Employee List"
                                tooltip="Allows access to see employees who were active OR new hire within a searchable time period"
                            />

                            <InfoCheckbox
                                id="chkEmployeeImportSwitchEnabled"
                                checked={employeeImportSwitchEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EmployeeImportSwitchEnabled,
                                    event.target.checked.toString(),
                                )}
                                badge={NewFieldBadge}
                                color="warning"
                                label="Enable Employee Import Switch"
                                tooltip='Adds a checkbox to the Employee form titled "Include with Import Updates"'
                            />

                            <SubHeading>Equipment</SubHeading>

                            <InfoCheckbox
                                id="chkenableEquipmentTags"
                                checked={
                                    enableEquipmentTags
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableEquipmentTags,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Equipment Tags"
                                tooltip="Enable tagging individual pieces of equipment"
                                color="warning"
                                badge={NewFieldBadge}
                            />

                            <InfoCheckbox
                                id="chkEnableAFADChargeTypeGrouping"
                                checked={enableAFADChargeTypeGrouping}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableAFADChargeTypeGrouping,
                                    event.target.checked.toString(),
                                )}
                                label="Enable AFAD Charge Type Grouping"
                                tooltip="Adds a &quot;Num AFADs In Set&quot; field to the Charge Type form for enabling billing units by set"
                                badge={FeatureBadge}
                            />

                            <SubHeading>Roster</SubHeading>

                            <InfoCheckbox
                                id="chkShowLongTermStatusCountOnRoster"
                                checked={showLongTermStatusCountOnRoster}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.ShowLongTermStatusCountOnRoster,
                                    event.target.checked.toString(),
                                )}
                                label="Show Long Term Status Count On Roster"
                                tooltip="Updates the Roster view to include counts of every long term status"
                                color="dark"
                                badge={SettingBadge}
                            />

                            <InfoCheckbox
                                id="chkEmployeeLongTermStatusManagementEnabled"
                                checked={
                                    employeeLongTermStatusManagementEnabled
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EmployeeLongTermStatusManagementEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Employee Long-Term Status Management"
                                tooltip="Allows for setting long-term statuses in the roster view and employee form"
                                badge={FeatureBadge}
                            />

                            <InfoCheckbox
                                id="chkenableCertification"
                                checked={
                                    enableCertification
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableCertification,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Certifications"
                                tooltip="Enable all certification features"
                                badge={FeatureBadge}
                            >
                                <InfoCheckbox
                                    id="chkEnablePayIncentivesforCertifications"
                                    checked={enablePayIncentivesforCertifications}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnablePayIncentivesforCertifications,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Pay Incentives for Certifications"
                                    tooltip="When on, employees with eligible certifications receive additional pay based on incentive settings."
                                    color="warning"
                                    badge={NewFieldBadge}
                                />
                            </InfoCheckbox>

                            <SubHeading>Holidays</SubHeading>

                            <InfoCheckbox
                                id="chkenableHoliday"
                                checked={
                                    enableCustomizedHoliday
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableCustomizedHoliday,
                                    event.target.checked.toString(),
                                )}
                                label="Define Company/County Holidays"
                                tooltip="Adds fields for managing custom holidays to the Customer org and County Group forms"
                                badge={NewFieldBadge}
                                color="warning"
                            />

                            <SubHeading
                                id="jobs"
                                onIntersect={this.handleIntersection}
                            >
                                Jobs
                            </SubHeading>

                            <InfoCheckbox
                                id="chkJobUploads"
                                checked={jobUploadsEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.JobUploadsEnabled,
                                    event.target.checked.toString(),
                                )}
                                color="warning"
                                badge={NewFieldBadge}
                                label="Enable Job File Attachments"
                                tooltip="Allows uploading attachments in the job form and displays the attachments on job assignments and timesheets"
                            />

                            <ValidatedSelect
                                id="jobTypes"
                                name="tenant.jobTypes"
                                label="Optional Job Type(s)"
                                isMulti
                                options={optionalJobTypes || []}
                                value={(optionalJobTypes || []).filter(
                                    (x) => (tenant.jobTypes || []).includes(x.value),
                                ) || ''}
                                onChange={this.onSelectChange}
                                helpMessage="Select which optional job types should be available for this tenant"
                            />

                            <InfoCheckbox
                                id="chkEnableRequestedPersonnel"
                                checked={enableRequestedPersonnel}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableRequestedPersonnel,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Requested Personnel"
                                tooltip="Adds separate resource fields to the Job form whenever AFADs are allocated to a job"
                                badge={NewFieldBadge}
                                color="warning"
                            />
                            <InfoCheckbox
                                id="chkRequireResourceKitOnJobBuild"
                                checked={requireResourceKitOnJobBuild}
                                onChange={(event) => {
                                    const isChecked = event.target.checked;
                                    this.onSettingChanged(
                                        TenantSettings.RequireResourceKitOnJobBuild,
                                        isChecked.toString(),
                                    );
                                }}
                                label="Require resource kit on job build"
                                tooltip="Requires selecting a resource kit when building a job and displays the resources included in the selected kit"
                                badge={FeatureBadge}
                            />

                            <InfoCheckbox
                                id="chkEnableJobOverrides"
                                checked={enableJobOverrides}
                                onChange={(event) => {
                                    const isChecked = event.target.checked;
                                    this.onSettingChanged(
                                        TenantSettings.EnableJobOverrides,
                                        isChecked.toString(),
                                    );
                                    if (!isChecked) {
                                        this.onSettingChanged(
                                            TenantSettings.DisableJobOverrideTimeRestriction,
                                            'false',
                                        );
                                    }
                                }}
                                label="Enable Overriding Job Details Per-Day"
                                tooltip="Allows adjusting job details from the Job form for every day that resources have been allocated"
                                badge={FeatureBadge}
                            >
                                <InfoCheckbox
                                    id="disableJobOverride"
                                    checked={disableJobOverrideTimeRestriction}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.DisableJobOverrideTimeRestriction,
                                        event.target.checked.toString(),
                                    )}
                                    label="Disable Job Override Time Restriction Check"
                                    tooltip="The flag used to enable/disable time restrictions for job overrides. Set to true to allow overrides at any time."
                                    color="dark"
                                    badge={SettingBadge}
                                />
                            </InfoCheckbox>

                            <FormGroup>
                                <FormLabel label="Job Title Template" />
                                <p className="text-muted small mb-1">
                                    Available tokens:
                                    {' '}
                                    <code>[[JobDescription]]</code>
                                    ,
                                    {' '}
                                    <code>[[SubcontractorCompanyName]]</code>
                                    ,
                                    {' '}
                                    <code>[[ForemanName]]</code>
                                    ,
                                    {' '}
                                    <code>[[JobStartTime]]</code>
                                    ,
                                    {' '}
                                    <code>[[WorkOrderNumber]]</code>
                                    ,
                                    {' '}
                                    <code>[[CustomerName]]</code>
                                    ,
                                    {' '}
                                    <code>[[Address]]</code>
                                    ,
                                    {' '}
                                    <code>[[TypeOfWork]]</code>
                                </p>
                                <Input
                                    type="textarea"
                                    rows={2}
                                    name={TenantSettings.JobTitleTemplate}
                                    value={jobTitleTemplate}
                                    onChange={(e) => this.onSettingChanged(TenantSettings.JobTitleTemplate, e.target.value)}
                                />
                            </FormGroup>

                            {this.renderSmsTemplatesEditor({
                                assignment: jobAssignmentSMSTemplate,
                                override: jobAssignmentOverrideSMSTemplate,
                                cancellation: jobAssignmentCancellationSMSTemplate,
                                clientCancellation: jobAssignmentClientCancellationSMSTemplate,
                                timesheetReject: timesheetRejectSMSTemplate,
                            })}

                            <SubHeading>
                                Dispatching
                            </SubHeading>

                            <InfoCheckbox
                                id="chkCrossDispatching"
                                checked={allowCrossDispatching}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.AllowCrossDispatching,
                                    event.target.checked.toString(),
                                )}
                                label="Allow Cross Dispatching of Resources"
                                tooltip="Allows schedulers/admin to dispatch any resource from any office to any job, rather than just the resources available to the job&apos;s office"
                                badge={FeatureBadge}
                            />

                            <InfoCheckbox
                                id="chkHours"
                                checked={hoursEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.DispatchHoursEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Hours Display"
                                tooltip="Displays daily and weekly hours worked in the Resources pane"
                                badge={FeatureBadge}
                            />

                            <InfoCheckbox
                                id="dispatchStatusFilterEnabled"
                                checked={dispatchStatusFilterEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.DispatchStatusFilterEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Resource Filtering"
                                tooltip="Allows filtering resources in the dispatch board"
                                badge={FeatureBadge}
                            />

                            <InfoCheckbox
                                id="chkAttendanceEventsEnabled"
                                checked={attendanceEventsEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.AttendanceEventsEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Attendance Events"
                                tooltip="Adds the Daily Attendance dashboard and adds reporting to the dispatch board and daily roster board"
                                badge={FeatureBadge}
                            />

                            <NumericInput
                                label="Assignment Cancellation Threshold"
                                required
                                ref={this.asncnlRef}
                                name="assignmentCancellationThreshold"
                                value={assignmentCancelThresh}
                                onChange={(event) => {
                                    const { value } = event.target;
                                    this.onSettingChanged(
                                        TenantSettings.AssignmentCancellationThreshold,
                                        value,
                                    );
                                }}
                                placeholder="Enter Threshold Minutes"
                                step="1"
                                max={999 ?? ''}
                                wholeNumber
                            />
                            <NumericInput
                                label="Assignment Cancellation Minimum Charge"
                                required
                                ref={this.asncnlMinRef}
                                name="assignmentCancellationMinimumCharge"
                                value={assignmentCancelMin}
                                onChange={(event) => {
                                    const { value } = event.target;
                                    this.onSettingChanged(
                                        TenantSettings.AssignmentCancellationMinimumCharge,
                                        value,
                                    );
                                }}
                                placeholder="Enter Minimum Minutes"
                                type="number"
                                step="1"
                                max={999 ?? ''}
                                wholeNumber
                            />
                            <InfoCheckbox
                                id="useRadioStyleDaySelection"
                                checked={useRadioStyleDaySelection}
                                onChange={(e) =>
                                    this.onSettingChanged(
                                        TenantSettings.UseRadioStyleDaySelection,
                                        e.target.checked.toString(),
                                    )}
                                label="Use radio-style day selection"
                                tooltip="Click to select one day (deselects others). Hold Ctrl/Cmd to add or remove days, or Shift to select a continuous range."
                                badge={FeatureBadge}
                            />

                            <SubHeading>Daily Job View</SubHeading>

                            <FormGroup>
                                <InfoCheckbox
                                    id="includeSchedulingOnDailyJobExport"
                                    checked={includeSchedulingOnDailyJobExport}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.IncludeSchedulingOnDailyJobExport,
                                        event.target.checked.toString(),
                                    )}
                                    label="Include Job Scheduling Column in Excel Export"
                                />
                            </FormGroup>

                            <SubHeading>Timesheets</SubHeading>

                            <InfoCheckbox

                                id="chkDriverTimesheetAcknowledgement"
                                checked={enableDriverRideConfirmationFields}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableDriverRideConfirmationFields,
                                    event.target.checked.toString(),
                                )}
                                color="warning"
                                badge={NewFieldBadge}
                                label="Enable Driver Ride Confirmation Fields"
                                tooltip="
                                    Displays fields for drivers to confirm ride disclaimers and directives on timesheets.
                                    Toggle this setting to include or exclude driver-related ride acknowledgments in timesheet reports.
                                "
                            />

                            <InfoCheckbox
                                id="chkDriverTimesheetReportingEnabled"
                                checked={enableDriverReportingInTimesheet}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableDriverReportingInTimesheet,
                                    event.target.checked.toString(),
                                )}
                                label="Include Assigned Driver in Timesheet Reports"
                                tooltip="Includes the assigned driver for vehicles that are billed hourly"
                                color="dark"
                                badge={SettingBadge}
                            />

                            <InfoCheckbox
                                id="chkTimesheetAdjustmentsEnabled"
                                checked={timesheetAdjustmentsEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.TimesheetAdjustmentsEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Timesheet Adjustments"
                                badge={FeatureBadge}
                                tooltip="Allows for admins to adjust timesheets after they have been submitted by the crew lead"
                            />

                            <InfoCheckbox
                                id="chkEnablePredefinedTimesheetMissingSigReason"
                                checked={
                                    enablePredefinedTimesheetMissingSigReason
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnablePredefinedTimesheetMissingSigReason,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Timesheet Predefined Missing Signature Reasons"
                                tooltip="Displays a list of reasons when &quot;No Signature&quot; is selected for a timesheet client review"
                                color="warning"
                                badge={NewFieldBadge}
                            />

                            <InfoCheckbox
                                id="chkTimesheetEmergencyEnabled"
                                checked={timesheetEmergencyEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.TimesheetEmergencyEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable timesheet emergency start/end times"
                                tooltip="Jobs that start within the timesheet emergency start/end times may be set by dispatch or timesheet admins as an emergency job."
                                badge={FeatureBadge}
                            >
                                <Input
                                    label="Timesheet Emergency Start Time"
                                    id="startTime"
                                    required={timesheetEmergencyEnabled}
                                    type="time"
                                    name="timesheetEmergencyStart"
                                    value={timesheetEmergencyStart ?? ''}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.TimesheetEmergencyStart,
                                        event.target.value,
                                    )}
                                    readOnly={!timesheetEmergencyEnabled}
                                    error="Start Time is required."
                                />

                                <Input
                                    label="Timesheet Emergency End Time"
                                    id="endTime"
                                    required={timesheetEmergencyEnabled}
                                    type="time"
                                    name="timesheetEmergencyEnd"
                                    value={timesheetEmergencyEnd ?? ''}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.TimesheetEmergencyEnd,
                                        event.target.value,
                                    )}
                                    readOnly={!timesheetEmergencyEnabled}
                                    error="End Time is required."
                                />

                                <ValidatedSelect
                                    id="timesheetEmergencyPayTypeId"
                                    name="timesheetEmergencyPayTypeId"
                                    label="How are emergency hours paid?"
                                    hideClear
                                    options={timesheetEmergencyPayTypes}
                                    value={(timesheetEmergencyPayTypes ?? []).filter(
                                        (x) => x.value == timesheetEmergencyPayTypeId ?? [],
                                    )}
                                    onChange={(item) => this.onSettingChanged(
                                        TenantSettings.TimesheetEmergencyPayTypeId,
                                        item.value,
                                    )}
                                    error="An emergency pay type selection is required."
                                    required
                                />

                                <UncontrolledTooltip
                                    placement="left"
                                    target="timesheetEmergencyPayTypeId"
                                >
                                    Sets the way in which emergency hours are paid out.
                                    This option affects the names of columns in Payroll: Pay Categories and pay calculations.
                                </UncontrolledTooltip>

                                <Collapse isOpen={timesheetEmergencyPayTypeId === TimesheetEmergencyPayTypes.Adjustment}>
                                    <NumericInput
                                        id="timesheetEmergencyPayAdjustment"
                                        label="Emergency Work Pay Adjustment"
                                        name="timesheetEmergencyPayAdjustment"
                                        value={timesheetEmergencyPayAdjustment}
                                        startAddon={(
                                            <span className="text-muted">$</span>
                                        )}
                                        onChange={(event) => {
                                            const { value } = event.target;
                                            this.onSettingChanged(
                                                TenantSettings.TimesheetEmergencyPayAdjustment,
                                                value,
                                            );
                                        }}
                                        placeholder="Enter an amount (in dollars)"
                                        step={0.01}
                                        required={timesheetEmergencyPayTypeId === TimesheetEmergencyPayTypes.Adjustment}
                                    />
                                </Collapse>

                                <UncontrolledTooltip
                                    placement="left"
                                    target="timesheetEmergencyPayAdjustment"
                                >
                                    Emergency hours worked will be paid at their regular rates, plus this adjustment.
                                </UncontrolledTooltip>
                            </InfoCheckbox>

                            <InfoCheckbox
                                id="chkEnableSelfDispatchTimesheets"
                                checked={enableSelfDispatchTimesheets}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableSelfDispatchTimesheets,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Non-Flagging Timesheets"
                                tooltip="Allows access to non-flagging, or &quot;self-dispatching&quot;, timesheets"
                                badge={FeatureBadge}
                            />

                            <InfoCheckbox
                                id="chkTimesheetExportEnabled"
                                checked={timesheetExportEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.TimesheetExportEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Timesheet Export"
                                tooltip="Adds an &quot;Export&quot; button to the Timesheet Management page"
                            />

                            <InfoCheckbox
                                id="chkForceAllChargesInTimesheetExport"
                                checked={forceAllChargesInTimesheetExport}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.ForceAllChargesInTimesheetExport,
                                    event.target.checked.toString(),
                                )}
                                color="dark"
                                badge={SettingBadge}
                                label="Force All Charges to Appear in Timesheet Report"
                                tooltip="
                                    By default, the Timesheet Report only includes billable charges.
                                    Turning this option on ensures all charges appear on the exported PDF.
                                    Note that this option only affects flagging timesheets exported from Timesheet Management.
                                "
                            />

                            <InfoCheckbox
                                id="chkTimesheetUseJobCity"
                                checked={timesheetUseJobCity}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.TimesheetUseJobCity,
                                    event.target.checked.toString(),
                                )}
                                label="Show Job City and Enforce on Approval"
                                tooltip="Requires the &quot;Job City&quot; field for timesheet approvals. Note: a system administrator is required to add new city options to the dropdown"
                                color="warning"
                                badge={NewFieldBadge}
                            />

                            <InfoCheckbox
                                id="chkAllowTimesheetStatusRollbacks"
                                checked={allowTimesheetStatusRollbacks}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.AllowTimesheetStatusRollbacks,
                                    event.target.checked.toString(),
                                )}
                                color="dark"
                                badge={SettingBadge}
                                label="Allow Timesheet Statuses To Be Rolled Back"
                                tooltip="Allows users to roll timesheets in the Approved and Exported status back to Submitted"
                            />

                            <InfoCheckbox
                                id="chkShowContractDescriptionInTimesheetView"
                                checked={
                                    showContractDescriptionInTimesheetView
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.ShowContractDescriptionInTimesheetView,
                                    event.target.checked.toString(),
                                )}
                                color="dark"
                                badge={SettingBadge}
                                label="Show Contract Description"
                                tooltip="All places that display jobs also display the job - i.e. contract - name"
                            />

                            <InfoCheckbox
                                id="chktimesheetEquipmentAuditEnabled"
                                checked={
                                    timesheetEquipmentAuditEnabled
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.TimesheetEquipmentAuditEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable timesheet equipment audit logs and driver tracking"
                                tooltip="Sets whether the user can view the timesheet equipment audit logs."
                                badge={FeatureBadge}
                            />

                            <InfoCheckbox
                                id="chktimesheetFlagEnabled"
                                checked={
                                    timesheetFlagEnabled
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.TimesheetFlagEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable timesheet flag"
                                tooltip="Used to display Flag in Timesheet Management Page."
                                badge={SettingBadge}
                                color="dark"
                            />

                            <InfoCheckbox
                                id="chkdisplayJobTypeInTimesheetManagement"
                                checked={
                                    displayJobTypeInTimesheetManagement
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.DisplayJobTypeInTimesheetManagement,
                                    event.target.checked.toString(),
                                )}
                                label="Display Job Type in Timesheet Management"
                                tooltip="Displays a Job Type column in the Timesheet Management page."
                                badge={SettingBadge}
                                color="dark"
                            />

                            <InfoCheckbox
                                id="retainClientSignatureOnUpdate"
                                checked={
                                    retainClientSignatureOnUpdate
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.RetainClientSignatureOnUpdate,
                                    event.target.checked.toString(),
                                )}
                                label="Retain Client Signature in Timesheet"
                                tooltip="If enabled, the client signature is preserved when a timesheet is updated, so the client doesn’t have to re-sign after changes."
                                badge={SettingBadge}
                                color="dark"
                            />

                            <InfoCheckbox
                                id="showTimesheetStatusHistory"
                                checked={
                                    showTimesheetStatusHistory
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.ShowTimesheetStatusHistory,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Timesheet Status History"
                                tooltip="Displays the Timesheet Status History in Timesheet Management when enabled."
                                color="dark"
                                badge={SettingBadge}
                            />

                            <InfoCheckbox
                                id="chkEnablePreJobForm"
                                checked={enablePreJobForm}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnablePreJobForm,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Pre-Job Form"
                                tooltip="Enable the ability to fill out the pre-job form in timesheets."
                                badge={FeatureBadge}
                            />

                            <InfoCheckbox
                                className="mt-2 mb-2"
                                id="chkAllowTimesheetAttachments"
                                checked={allowTimesheetAttachments}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.AllowTimesheetAttachments,
                                    event.target.checked.toString(),
                                )}
                                label="Allow Timesheet Attachments"
                                tooltip={`Allows ${crewLeadLabel(tenant?.tenantSettings)} to add file attachments to a Timesheet when submitting.`}
                                badge={FeatureBadge}
                            />

                            <InfoCheckbox
                                id="enablePaintBatchCodes"
                                checked={enablePaintBatchCodes}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnablePaintBatchCodes,
                                    event.target.checked.toString(),
                                )}
                                badge={NewFieldBadge}
                                color="warning"
                                label="Enable Paint Batch Codes"
                                tooltip="Adds a Paint Batch Code section to the timesheet, where field staff enter the batch codes for the paint they used and upload photos of the paint labels."
                            />

                            <SubHeading
                                id="netsuite"
                                onIntersect={this.handleIntersection}
                            >
                                NetSuite
                            </SubHeading>

                            <InfoCheckbox
                                id="chkEnableEstimate"
                                checked={enableEstimate}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableEstimate,
                                    event.target.checked.toString(),
                                )}
                                badge={FeatureBadge}
                                label="Enable creating contracts from NetSuite Estimates"
                                tooltip="Display NetSuite estimates and allow creating contract from an estimate."
                            />

                            <Input
                                label="NetSuite Invoice Form Id"
                                id="netSuiteInvoiceForm"
                                required={invoicingEnabled}
                                name="netSuiteInvoiceForm"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.NetSuiteInvoiceForm,
                                    event.target.value,
                                )}
                                value={netSuiteInvoiceForm}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="netSuiteInvoiceForm"
                            >
                                NetSuite internal ID for the Invoice Form.
                                This ID determines which custom form is used to generate invoices.
                            </UncontrolledTooltip>

                            <Input
                                label="NetSuite IC Invoice Form Id"
                                id="netSuiteICInvoiceForm"
                                required={invoicingEnabled}
                                name="netSuiteICInvoiceForm"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.NetSuiteICInvoiceForm,
                                    event.target.value,
                                )}
                                value={netSuiteICInvoiceForm}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="netSuiteICInvoiceForm"
                            >
                                NetSuite internal ID for the Intercompany Invoice Form.
                                This ID determines which custom form is used to generate intercompany invoices.
                            </UncontrolledTooltip>

                            <Input
                                label="NetSuite IC End customer Invoice Form Id"
                                id="netSuiteICEndInvoiceForm"
                                required={invoicingEnabled}
                                name="netSuiteICEndInvoiceForm"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.NetSuiteICEndInvoiceForm,
                                    event.target.value,
                                )}
                                value={netSuiteICEndInvoiceForm}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="netSuiteICEndInvoiceForm"
                            >
                                NetSuite internal ID for the Intercompany End Customer Invoice Form.
                                This ID determines which custom form is used to generate intercompany End Customer invoices.
                            </UncontrolledTooltip>

                            <Input
                                label="NetSuite IC Office Id"
                                id="netSuiteICOffice"
                                required={invoicingEnabled}
                                name="netSuiteICOffice"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.NetSuiteICOffice,
                                    event.target.value,
                                )}
                                value={netSuiteICOffice}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="netSuiteICOffice"
                            >
                                NetSuite internal ID for the Intercompany Office.
                                This ID maps to the office field on NetSuite intercompany invoices.
                            </UncontrolledTooltip>

                            <Input
                                label="NetSuite Description Item Id"
                                id="netSuiteDescriptionItem"
                                required={invoicingEnabled}
                                name="netSuiteDescriptionItem"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.NetSuiteDescriptionItem,
                                    event.target.value,
                                )}
                                value={netSuiteDescriptionItem}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="netSuiteDescriptionItem"
                            >
                                NetSuite internal ID for the Description Item.
                                This ID will be used when adding header line items to NS invoices.
                            </UncontrolledTooltip>

                            <Input
                                label="NetSuite IC Description Item Id"
                                id="netSuiteICDescriptionItem"
                                required={invoicingEnabled}
                                name="netSuiteICDescriptionItem"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.NetSuiteICDescriptionItem,
                                    event.target.value,
                                )}
                                value={netSuiteICDescriptionItem}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="netSuiteICDescriptionItem"
                            >
                                NetSuite internal ID for the Intercompany Description Item.
                                This ID will be used when adding header line items to NS Intercompany invoices.
                            </UncontrolledTooltip>

                            <Input
                                label="NetSuite Percentage Item Id"
                                id="netSuitePercentageItem"
                                required={invoicingEnabled}
                                name="netSuitePercentageItem"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.NetSuitePercentageItem,
                                    event.target.value,
                                )}
                                value={netSuitePercentageItem}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="netSuitePercentageItem"
                            >
                                NetSuite internal ID for the Percentage Item.
                                This ID will be used when adding percentage line items to NS invoices.
                            </UncontrolledTooltip>

                            <Input
                                label="NetSuite Multi Subcontractor Id"
                                id="netSuiteMultiSubcontractor"
                                required={invoicingEnabled}
                                name="netSuiteMultiSubcontractor"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.NetSuiteMultiSubcontractor,
                                    event.target.value,
                                )}
                                value={netSuiteMultiSubcontractor}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="netSuiteMultiSubcontractor"
                            >
                                NetSuite internal ID for Multiple Subcontractors.
                                This ID will be mapped to the subcontractor field in NetSuite when an invoice has multiple subcontractors.
                            </UncontrolledTooltip>

                            <InfoCheckbox
                                id="netSuiteInvoiceCustomerPickup"
                                checked={netSuiteInvoiceCustomerPickup}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.NetSuiteInvoiceCustomerPickup,
                                    event.target.checked.toString(),
                                )}
                                label="Mark Rental Invoices as Customer Pickup in NetSuite"
                            />

                            <SubHeading
                                id="payroll"
                                onIntersect={this.handleIntersection}
                            >
                                Payroll
                            </SubHeading>

                            <InfoCheckbox
                                id="chkPayrollEnabled"
                                checked={payrollEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.PayrollEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Payroll"
                                tooltip="Adds the Payroll feature, which includes the Payroll: Charges and Payroll: Pay Categories pages"
                                badge={FeatureBadge}
                            >
                                <InfoCheckbox
                                    id="chkEnableSundayOT"
                                    checked={enableSundayOT}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableSundayOT,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Automatic T15 for Sundays"
                                    tooltip="Hours worked on Sundays will be considered T15 (time-and-a-half) hours."
                                    color="dark"
                                    badge={SettingBadge}
                                />

                                <InfoCheckbox
                                    id="chkEnableHolidayOT"
                                    checked={enableHolidayOT}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableHolidayOT,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Automatic T15 for Holidays"
                                    tooltip="Hours worked on Holidays will be considered T15 (time-and-a-half) hours."
                                    color="dark"
                                    badge={SettingBadge}
                                />

                                <InfoCheckbox
                                    id="chkEnableDoubleTimePayroll"
                                    checked={enableDoubleTimePayroll}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableDoubleTimePayroll,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Double-Time Payroll"
                                    tooltip="Hours worked on Sundays (depending on contract) and Holidays will be considered double-time (DT) hours."
                                    color="dark"
                                    badge={SettingBadge}
                                />

                                {enableDoubleTimePayroll && (
                                    <InfoCheckbox
                                        id="applyDTtoFullShift"
                                        checked={applyDTtoFullShift}
                                        onChange={(event) => this.onSettingChanged(
                                            TenantSettings.ApplyDTtoFullShift,
                                            event.target.checked.toString(),
                                        )}
                                        label="Apply DT to Entire Shift When Start Day Is DT-Eligible"
                                        tooltip="All hours for jobs that start on Sunday/Holiday will be paid as Double Time (DT)."
                                        color="dark"
                                        badge={SettingBadge}
                                    />
                                )}

                                <InfoCheckbox
                                    id="chkEnableDriverPaySources"
                                    checked={driverPaySourcesEnabled}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableDriverPaySources,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Driver and Passenger Pay Sources"
                                    tooltip="Adds Driver and Passenger pay sources to union charge types and adds driver and passenger pay rates to States."
                                    color="warning"
                                    badge={NewFieldBadge}
                                />

                                <InfoCheckbox
                                    id="chkEnableWAHR"
                                    checked={enableWAHR}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnableWAHR,
                                        event.target.checked.toString(),
                                    )}
                                    label="Enable Weighted Average Rate for Non-PW Overtime"
                                    tooltip="Calculates non-prevailing wage overtime at 1.5 times the employee’s weighted average hourly rate for the week."
                                    color="warning"
                                    badge={NewFieldBadge}
                                />

                                <InfoCheckbox
                                    id="chkEnablePayrollStatusInChargeView"
                                    checked={enablePayrollStatusInChargeView}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnablePayrollStatusInChargeView,
                                        event.target.checked.toString(),
                                    )}
                                    label="Show Payroll Status in Charge View"
                                    tooltip="Shows payroll status column in the Charge View table to track timesheet export status."
                                    color="dark"
                                    badge={SettingBadge}
                                />

                                <InfoCheckbox
                                    id="chkEnablePremiumPayroll"
                                    checked={enablePremiumPayroll}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.EnablePremiumPayroll,
                                        event.target.checked.toString(),
                                    )}
                                    label="Emergency and Holiday hours are paid at premium rates"
                                    tooltip="Adds PR and PROT payroll categories. If shift differential payroll is enabled, also adds the PRSD and PROTSD categories."
                                    color="dark"
                                    badge={SettingBadge}
                                />
                            </InfoCheckbox>

                            <ValidatedSelect
                                id="payPeriodBeginDayOfWeek"
                                name="payPeriodBeginDayOfWeek"
                                label="Week Starts On"
                                placeholder="Select the day the week starts on"
                                error="Select the day the week starts on"
                                required
                                options={daysOfWeek}
                                value={weekStartDay}
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.name}
                                onChange={(item) => this.onSettingChanged(
                                    TenantSettings.WeekStartsOn,
                                    item.id,
                                )}
                            />

                            {Boolean(weekStartDay) && Boolean(weekEndDay) && (
                                <small className="form-text text-muted mb-3">
                                    {`Weeks run ${weekStartDay.name} through ${weekEndDay.name}.`}
                                </small>
                            )}

                            {this.renderSettingRefusal('payPeriodBeginDayOfWeek')}

                            <UncontrolledTooltip
                                placement="left"
                                target="payPeriodBeginDayOfWeek"
                            >
                                The day every week begins on for dispatch, jobs and payroll. It can only be changed before the tenant has any jobs, bill periods or payroll.
                            </UncontrolledTooltip>

                            <ValidatedSelect
                                id="payPeriodTypeId"
                                name="payPeriodTypeId"
                                options={payPeriodTypes}
                                label="Pay Period Duration"
                                hideClear
                                value={(payPeriodTypes ?? []).filter(
                                    (x) => x.value == payPeriodTypeId ?? [],
                                )}
                                onChange={(item) => this.onSettingChanged(
                                    TenantSettings.PayPeriodTypeId,
                                    item.value,
                                )}
                                error="A pay period duration selection is required."
                                required
                            />

                            {this.renderSettingRefusal('payPeriodTypeId')}

                            <UncontrolledTooltip
                                placement="left"
                                target="payPeriodTypeId"
                            >
                                Sets the behaviour of exports and &quot;Week of&quot; selectors in Payroll: Charges and Payroll: Pay Categories
                            </UncontrolledTooltip>

                            <Collapse isOpen={payPeriodTypeId === PayPeriodTypes.Biweekly}>
                                <Input
                                    label="Biweekly Pay Period Start Date"
                                    id="biweeklyPayPeriodStartDate"
                                    required={payPeriodTypeId === PayPeriodTypes.Biweekly}
                                    type="date"
                                    name="biweeklyPayPeriodStartDate"
                                    value={biweeklyPayPeriodStartDate ?? ''}
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.BiweeklyPayPeriodStartDate,
                                        // Pay periods begin on the first day of a tenant week, so snap to it.
                                        event.target.value ? getWeekStartDate(event.target.value, weekStartsOn) : '',
                                    )}
                                    error="You must enter a start date for bi-weekly pay periods"
                                />

                                {this.renderSettingRefusal('biweeklyPayPeriodStartDate')}

                                <UncontrolledTooltip
                                    placement="left"
                                    target="biweeklyPayPeriodStartDate"
                                >
                                    All employees in this tenant will have their pay periods synced based on this start date.
                                    Every two weeks from this date will begin a new period.
                                    {weekStartDay ? ` The date snaps to the ${weekStartDay.name} that starts its week.` : ''}
                                </UncontrolledTooltip>
                            </Collapse>

                            <InfoCheckbox
                                id="chkEnableRegularShiftDifferential"
                                checked={enableRegularShiftDifferential}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableRegularShiftDifferential,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Payable Shift Differential Hours by Default?"
                                tooltip="By default, jobs worked outside of regular hours will be treated as shift differential jobs and paid as such."
                                badge={SettingBadge}
                                color="dark"
                            >
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="regularShiftDifferentialStart"
                                        label="Shift Differential Start Time"
                                        required
                                    />
                                    <TimeInput
                                        id="regularShiftDifferentialStart"
                                        required={enableRegularShiftDifferential}
                                        name="regularShiftDifferentialStart"
                                        value={regularShiftDifferentialStart ?? ''}
                                        onChange={(event) => this.onSettingChanged(
                                            TenantSettings.RegularShiftDifferentialStart,
                                            event.target.value,
                                        )}
                                        fullWidth
                                        size="lg"
                                        increment={
                                            0.25
                                        }
                                    />
                                    <small className="invalid-feedback text-danger">
                                        Start Time is required.
                                    </small>
                                </FormGroup>

                                <FormGroup className="mb-0">
                                    <FormLabel
                                        htmlFor="regularShiftDifferentialEnd"
                                        label="Shift Differential End Time"
                                        required
                                    />
                                    <TimeInput
                                        id="regularShiftDifferentialEnd"
                                        required={enableRegularShiftDifferential}
                                        name="regularShiftDifferentialEnd"
                                        value={regularShiftDifferentialEnd ?? ''}
                                        onChange={(event) => this.onSettingChanged(
                                            TenantSettings.RegularShiftDifferentialEnd,
                                            event.target.value,
                                        )}
                                        fullWidth
                                        size="lg"
                                        increment={
                                            0.25
                                        }
                                    />
                                    <small className="invalid-feedback text-danger">
                                        End Time is required.
                                    </small>
                                </FormGroup>
                            </InfoCheckbox>

                            <NumericInput
                                id="shiftDifferentialPayAdjustment"
                                label="Shift Differential Pay Adjustment"
                                name="shiftDifferentialPayAdjustment"
                                value={shiftDifferentialPayAdjustment}
                                startAddon={(
                                    <span className="text-muted">$</span>
                                )}
                                onChange={(event) => {
                                    const { value } = event.target;
                                    this.onSettingChanged(
                                        TenantSettings.ShiftDifferentialPayAdjustment,
                                        value,
                                    );
                                }}
                                placeholder="Enter an amount (in dollars)"
                                step="0.01"
                            />

                            <UncontrolledTooltip target="shiftDifferentialPayAdjustment">
                                Adjusts the pay rate of all employees working shift differential jobs by this amount.
                                e.g. flaggers will make an additional 30 cents/hr if you enter 0.30
                            </UncontrolledTooltip>

                            <InfoCheckbox
                                id="chkEnableWeekendShiftDifferential"
                                checked={enableWeekendShiftDifferential}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableWeekendShiftDifferential,
                                    event.target.checked.toString(),
                                )}
                                label="Weekends are always shift differential"
                                tooltip="Hours that occur on the weekend automatically become shift differential. Sunday will go into T15 instead of SD if Sunday T15 is enabled."
                                color="dark"
                                badge={SettingBadge}
                            />

                            <ValidatedSelect
                                id="payrollAppTypeId"
                                name="payrollAppTypeId"
                                label="Payroll Application"
                                options={PayrollAppTypeOptions}
                                value={(PayrollAppTypeOptions ?? []).filter(
                                    (x) => x.value == payrollAppTypeId ?? [],
                                )}
                                onChange={(item) => this.onSettingChanged(
                                    TenantSettings.PayrollAppTypeId,
                                    item.value,
                                )}
                            />

                            <UncontrolledTooltip
                                placement="left"
                                target="payrollAppTypeId"
                            >
                                For integrating with third-party payroll software. If the payroll used by this tenant is not in this list, please select None.
                            </UncontrolledTooltip>

                            <Input
                                label="Payroll Organization/Application ID"
                                id="payrollAppId"
                                name="payrollAppId"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.PayrollAppId,
                                    event.target.value,
                                )}
                                value={payrollAppId}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="payrollAppId"
                            >
                                The organization or application ID for this integration, if necessary. In Paychex, this corresponds to the Company ID.
                            </UncontrolledTooltip>

                            <Input
                                label="Payroll Application API Key"
                                id="payrollAppApiKey"
                                name="payrollAppApiKey"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.PayrollAppApiKey,
                                    event.target.value,
                                )}
                                value={payrollAppApiKey}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="payrollAppApiKey"
                            >
                                API Key for the Payroll app integration
                            </UncontrolledTooltip>

                            <Input
                                label="Payroll Application API Secret"
                                id="payrollAppApiSecret"
                                name="payrollAppApiSecret"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.PayrollAppApiSecret,
                                    event.target.value,
                                )}
                                value={payrollAppApiSecret}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="payrollAppApiSecret"
                            >
                                API Secret for the Payroll app integration, if it uses OAuth
                            </UncontrolledTooltip>

                            <Input
                                label="Certified Payroll Administrator Name"
                                id="certifiedPayrollReporterName"
                                name="certifiedPayrollReporterName"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.CertifiedPayrollReporterName,
                                    event.target.value,
                                )}
                                value={certifiedPayrollReporterName}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="certifiedPayrollReporterName"
                            >
                                The name that appears as a signatory in the Certified Payroll report
                            </UncontrolledTooltip>

                            <Input
                                label="Certified Payroll Administrator Title"
                                id="certifiedPayrollReporterTitle"
                                name="certifiedPayrollReporterTitle"
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.CertifiedPayrollReporterTitle,
                                    event.target.value,
                                )}
                                value={certifiedPayrollReporterTitle}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="certifiedPayrollReporterTitle"
                            >
                                The job title that appears as a signatory in the Certified Payroll report
                            </UncontrolledTooltip>

                            <InfoCheckbox
                                id="fringePaidInCash"
                                checked={fringePaidInCash}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.FringePaidInCash,
                                    event.target.checked.toString(),
                                )}
                                badge={FeatureBadge}
                                label="Fringe benefits paid in cash"
                                tooltip="Check this box if fringe benefits are paid in cash. This will exclude fringe benefits from the certified payroll report."
                            />

                            <InfoCheckbox
                                id="includeMedicareDeductionsInFICAColumn"
                                checked={includeMedicareDeductionsInFICAColumn}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.IncludeMedicareDeductionsInFICAColumn,
                                    event.target.checked.toString(),
                                )}
                                badge={FeatureBadge}
                                label="Include Medicare deductions in the FICA column of the Certified Payroll PDF report."
                                tooltip="Check this box if the medicare deductions should be included in the FICA column on the certified payroll PDF report."
                            />

                            <Button
                                disabled={!!this.state.saving}
                                color="primary"
                                name="importEmployees"
                                onClick={this.importADPEmployees}
                                outline
                            >
                                {this.state.saving ? 'Importing...' : 'Import ADP Employees'}
                            </Button>

                            <SubHeading
                                id="reporting"
                                onIntersect={this.handleIntersection}
                            >
                                Reporting
                            </SubHeading>

                            <InfoCheckbox
                                id="chkContractDateRange"
                                checked={enableContractDateRange}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableContractDateRange,
                                    event.target.checked.toString(),
                                )}
                                badge={FeatureBadge}
                                label="Enable Contract Effective Date Range"
                                tooltip="Allows for contracts to automatically expire at the end of the specified date range. If enabled, also allows sending contract expiry notifications to an email list."
                            >
                                <FormGroup>
                                    <FormLabel
                                        className="w-100 contract-email-label"
                                        htmlFor="contractExpiryDistributionList"
                                        label="Contract Expiry - Email Distribution List"
                                    />

                                    <TipAlert
                                        label={(
                                            <>
                                                Enter addresses that should receive expiring contract reports.
                                                Reports are sent every morning at 7:05am and include contracts whose
                                                expiry date is less than the warning threshold defined on the contract.
                                            </>
                                        )}
                                    />

                                    <EmailRecipients
                                        currentEmailAddress={
                                            currentContractExpiryDistributionList
                                        }
                                        onEmailAdded={() =>
                                            this.onEmailAdded(
                                                TenantSettings.ContractExpiryDistributionList,
                                                'currentContractExpiryDistributionList',
                                            )}
                                        onEmailChanged={this.onEmailChanged('currentContractExpiryDistributionList')}
                                        onEmailRemoved={this.onEmailRemoved(TenantSettings.ContractExpiryDistributionList)}
                                        emailAddresses={
                                            this.getTenantSetting(
                                                TenantSettings.ContractExpiryDistributionList,
                                            )?.split(',') ?? []
                                        }
                                    />
                                </FormGroup>
                                <Input
                                    label="Contract Expiry - Company Name"
                                    helpMessage={`Optional; if not set, the tenant company name ("${companyName}") will be used on the subject line.`}
                                    id="contractExpiryCompanyName"
                                    name="contractExpiryCompanyName"
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.ContractExpiryCompanyName,
                                        event.target.value,
                                    )}
                                    value={contractExpiryCompanyName}
                                    formGroupClassName="mb-0"
                                />
                            </InfoCheckbox>

                            <InfoCheckbox
                                id="chkTimesheetEmailEnabled"
                                checked={timesheetEmailEnabled}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.TimesheetEmailEnabled,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Emailing of Timesheets"
                                tooltip="Emails timesheets to a list of people determined on the contract whenever a timesheet is submitted"
                                badge={FeatureBadge}
                            >
                                <Input
                                    label="Timesheet Email From Name"
                                    id="timesheetEmailFromName"
                                    required={timesheetEmailEnabled}
                                    placeholder="ex: Support"
                                    autoComplete="off"
                                    name="timesheetEmailFromName"
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.TimesheetEmailFromName,
                                        event.target.value,
                                    )}
                                    value={timesheetEmailFromName}
                                />
                                <UncontrolledTooltip
                                    placement="left"
                                    target="timesheetEmailFromName"
                                >
                                    All timesheet emails will be sent from this name and address.
                                </UncontrolledTooltip>

                                <Input
                                    label="Timesheet Email From Email"
                                    id="timesheetEmailReplyTo"
                                    type="email"
                                    required={timesheetEmailEnabled}
                                    placeholder="ex: person@provider.com"
                                    autoComplete="off"
                                    name="timesheetEmailReplyTo"
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.TimesheetEmailReplyTo,
                                        event.target.value,
                                    )}
                                    value={timesheetEmailReplyTo}
                                />
                                <UncontrolledTooltip
                                    placement="left"
                                    target="timesheetEmailReplyTo"
                                >
                                    All timesheet emails will have this address in the &quot;From&quot; field
                                </UncontrolledTooltip>

                                <Input
                                    label="Timesheet Digest Email BCC"
                                    id="timesheetEmailBcc"
                                    type="text"
                                    placeholder="ex: person@provider.com,person2@provider.com"
                                    autoComplete="off"
                                    name="timesheetEmailBcc"
                                    onChange={(event) => this.onSettingChanged(
                                        TenantSettings.TimesheetEmailBcc,
                                        event.target.value,
                                    )}
                                    value={timesheetEmailBcc}
                                />
                                <UncontrolledTooltip
                                    placement="left"
                                    target="timesheetEmailBcc"
                                >
                                    Email addresses in this field will be BCC&apos;d on all digest emails.
                                </UncontrolledTooltip>
                            </InfoCheckbox>

                            <InfoCheckbox
                                id="chkHasHoursReport"
                                checked={hasHoursReport}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.HasHoursReport,
                                    event.target.checked.toString(),
                                )}
                                label="Enable Hours Report"
                                tooltip='Displays an "Hours Report" button in Charge View'
                                badge={FeatureBadge}
                            />

                            <InfoCheckbox
                                id="chkEnableStaffingReport"
                                checked={enableStaffingReport}
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableStaffingReport,
                                    event.target.checked.toString(),
                                )}
                                badge={FeatureBadge}
                                label="Enable Monthly Staffing Report"
                                tooltip="Sends a monthly report to the list of email addresses below. Report is generated as an excel sheet."
                            >
                                <FormGroup>
                                    <FormLabel
                                        className="w-100 contract-email-label"
                                        htmlFor="staffingReportDistributionList"
                                        label="Staffing Report - Email Distribution List"
                                    />

                                    <TipAlert
                                        label={(
                                            <>
                                                Enter addresses that should receive the monthly staffing report.
                                                The report is generated as an Excel sheet.
                                            </>
                                        )}
                                    />

                                    <EmailRecipients
                                        currentEmailAddress={
                                            currentStaffingReportDistributionList
                                        }
                                        onEmailAdded={() =>
                                            this.onEmailAdded(
                                                TenantSettings.StaffingReportDistributionList,
                                                'currentStaffingReportDistributionList',
                                            )}
                                        onEmailChanged={this.onEmailChanged('currentStaffingReportDistributionList')}
                                        onEmailRemoved={this.onEmailRemoved(TenantSettings.StaffingReportDistributionList)}
                                        emailAddresses={
                                            this.getTenantSetting(
                                                TenantSettings.StaffingReportDistributionList,
                                            )?.split(',') ?? []
                                        }
                                    />
                                </FormGroup>
                            </InfoCheckbox>

                            <InfoCheckbox
                                id="chkenableNewCustomerBillableHoursReport"
                                checked={
                                    enableNewCustomerBillableHoursReport
                                }
                                onChange={(event) => this.onSettingChanged(
                                    TenantSettings.EnableNewCustomerBillableHoursReport,
                                    event.target.checked.toString(),
                                )}
                                label="Send Weekly New Customer Billable Hours Report"
                                tooltip="Sends a weekly report email and also allows for manually running the report through the &quot;Collected Reporting&quot; administrative page"
                                badge={FeatureBadge}
                            >
                                <FormGroup>
                                    <FormLabel
                                        className="w-100 contract-email-label"
                                        htmlFor="newCustomerBillableHoursReportDistributionList"
                                        label="New Customer Billable Hours - Email Distribution List"
                                    />

                                    <TipAlert
                                        label={(
                                            <>
                                                Enter addresses that should receive the weekly new customer
                                                billable hours report.
                                            </>
                                        )}
                                    />

                                    <EmailRecipients
                                        currentEmailAddress={
                                            currentNewCustomerBillableHoursReportDistributionList
                                        }
                                        onEmailAdded={() =>
                                            this.onEmailAdded(
                                                TenantSettings.NewCustomerBillableHoursReportDistributionList,
                                                'currentNewCustomerBillableHoursReportDistributionList',
                                            )}
                                        onEmailChanged={this.onEmailChanged('currentNewCustomerBillableHoursReportDistributionList')}
                                        onEmailRemoved={this.onEmailRemoved(TenantSettings.NewCustomerBillableHoursReportDistributionList)}
                                        emailAddresses={
                                            this.getTenantSetting(
                                                TenantSettings.NewCustomerBillableHoursReportDistributionList,
                                            )?.split(',') ?? []
                                        }
                                    />
                                </FormGroup>
                            </InfoCheckbox>

                            <FormGroup>
                                <FormLabel
                                    className="w-100 contract-email-label"
                                    htmlFor="submittedTimesheetEmailList"
                                    label="Daily Timesheets Report Email Recipients"
                                />
                                <small className="pl-2 pb-2 text-success">
                                    Enter addresses at which you would like
                                    to receive daily timesheets report below.
                                </small>
                                <EmailRecipients
                                    currentEmailAddress={
                                        currentSubmittedTimesheetEmailList
                                    }
                                    onEmailAdded={() =>
                                        this.onEmailAdded(
                                            TenantSettings.SubmittedTimesheetsReportEmailList,
                                            'currentSubmittedTimesheetEmailList',
                                        )}
                                    onEmailChanged={this.onEmailChanged('currentSubmittedTimesheetEmailList')}
                                    onEmailRemoved={this.onEmailRemoved(TenantSettings.SubmittedTimesheetsReportEmailList)}
                                    emailAddresses={
                                        this.getTenantSetting(
                                            TenantSettings.SubmittedTimesheetsReportEmailList,
                                        )?.split(',') ?? []
                                    }
                                />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel
                                    className="w-100 contract-email-label"
                                    htmlFor="dailyGSFReportEmailList"
                                    label="Daily GSF Report Email Recipients"
                                />
                                <small className="pl-2 pb-2 text-success">
                                    Enter addresses at which you would like
                                    to receive daily GSF report below.
                                </small>
                                <EmailRecipients
                                    currentEmailAddress={
                                        currentDailyGSFReportEmailList
                                    }
                                    onEmailAdded={() =>
                                        this.onEmailAdded(
                                            TenantSettings.DailyGSFReportEmailList,
                                            'currentDailyGSFReportEmailList',
                                        )}
                                    onEmailChanged={this.onEmailChanged('currentDailyGSFReportEmailList')}
                                    onEmailRemoved={this.onEmailRemoved(TenantSettings.DailyGSFReportEmailList)}
                                    emailAddresses={
                                        this.getTenantSetting(
                                            TenantSettings.DailyGSFReportEmailList,
                                        )?.split(',') ?? []
                                    }
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel
                                    className="w-100 contract-email-label"
                                    htmlFor="dailyOperationalReportEmailList"
                                    label="Daily Operational Report Email Recipients"
                                />

                                <TipAlert
                                    label={(
                                        <>
                                            Enter addresses that should receive daily operational reports.
                                            Reports are sent each day at 9:30am, 5:30pm and 7:30pm.
                                        </>
                                    )}
                                />

                                <EmailRecipients
                                    currentEmailAddress={
                                        currentDailyOperationalReportEmailList
                                    }
                                    onEmailAdded={() =>
                                        this.onEmailAdded(
                                            TenantSettings.DailyOperationalReportEmailList,
                                            'currentDailyOperationalReportEmailList',
                                        )}
                                    onEmailChanged={this.onEmailChanged('currentDailyOperationalReportEmailList')}
                                    onEmailRemoved={this.onEmailRemoved(TenantSettings.DailyOperationalReportEmailList)}
                                    emailAddresses={
                                        this.getTenantSetting(
                                            TenantSettings.DailyOperationalReportEmailList,
                                        )?.split(',') ?? []
                                    }
                                    InputProps={{
                                        startAddon: (
                                            <>
                                                <IconButton
                                                    id="download-operational-report"
                                                    onClick={(e) => this.downloadDailyOperationalReport(e, null)}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faDownload}
                                                        className="text-muted"
                                                    />
                                                </IconButton>

                                                <UncontrolledTooltip target="download-operational-report">
                                                    Download Daily Operational Report
                                                </UncontrolledTooltip>
                                            </>
                                        ),
                                    }}
                                />
                            </FormGroup>

                            <Input
                                type="date"
                                label="Download Daily Operational Report For Specific Date"
                                value={this.state.selectedReportDate}
                                onChange={this.onReportDateChange}
                                max={new Date().toISOString().split('T')[0]}
                                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                style={{ cursor: 'pointer' }}
                                endAddonContained={false}
                                endAddon={(
                                    <>
                                        <Button
                                            id="download-operational-report-date"
                                            type="button"
                                            color="primary"
                                            disabled={!this.state.selectedReportDate}
                                            onClick={(e) => this.downloadDailyOperationalReport(e, this.state.selectedReportDate)}
                                        >
                                            Download
                                        </Button>
                                        <UncontrolledTooltip target="download-operational-report-date">
                                            Download Daily Operational Report for Selected Date
                                        </UncontrolledTooltip>
                                    </>
                                )}
                            />

                            <FormGroup>
                                <Button
                                    disabled={!!this.state.saving}
                                    size="sm"
                                    type="button"
                                    color="secondary"
                                    onClick={async () => {
                                        const url = ApiRoutes.tenant.invalidPhoneNumbers();
                                        await httpClient.downloadFile(
                                            url,
                                            null,
                                            `${this.state.tenant.companyName.replace(
                                                ' ',
                                                '_',
                                            )}_invalid_mobile_numbers.xlsx`,
                                        );
                                    }}
                                >
                                    <FontAwesomeIcon
                                        className="mr-2"
                                        icon={faPhone}
                                    />
                                    Get Invalid Mobile Numbers Report
                                </Button>
                            </FormGroup>

                        </Col>
                    </Row>
                </AppPageForm>
            </>
        );
    }
}
export default TenantForm;
